import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// Listed by tableau-mcp 4.27.2 on 2026-10-22. Its tools/list needs a session,
// which P0 does not hold when it syncs a gateway.
export const tableauMcpTools: McpTool[] = [
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Datasource Metadata",
    },
    description:
      "\n    This tool retrieves metadata for a datasource specified by its datasourceLuid, which may be a published or an embedded (workbook) data source. It takes the basic, high level, metadata results from Tableau's VizQL Data Service and enriches them with additional context provided by Tableau's Metadata API.\n    The metadata provided by this tool consists of the datasource model, fields, and parameters that belong to the datasource.\n    Fields will contain properties such as name and dataType. The richer Metadata API context (descriptions, dataCategories, roles, etc.) is only available for published data sources and may be absent for embedded ones.\n    For an embedded data source, also pass workbookLuid: the LUID of the workbook it belongs to. The server configuration may restrict access to embedded data sources based on their workbook.\n    This tool should be used for getting the metadata to ground the use of a tool that queries Tableau data sources.\n    ",
    inputSchema: {
      additionalProperties: false,
      properties: {
        datasourceLuid: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
        workbookLuid: {
          $ref: "#/properties/datasourceLuid",
          description:
            "Required to access an embedded (workbook) data source: the LUID of the workbook it belongs to, e.g. the workbook LUID passed to get-workbook. Omit for published data sources.",
        },
      },
      required: ["datasourceLuid"],
      type: "object",
    },
    name: "get-datasource-metadata",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Datasources",
    },
    description:
      '\n  Retrieves a list of published data sources from a specified Tableau site using the Tableau REST API. Supports optional filtering via field:operator:value expressions (e.g., name:eq:Views) for precise and flexible data source discovery.\n  To list results based on usage popularity or relevance, use the search-content tool instead.\n\n  **Supported Filter Fields and Operators**\n  | Field                  | Operators                                 |\n  |------------------------|-------------------------------------------|\n  | authenticationType     | eq, in                                    |\n  | connectedWorkbookType  | eq, gt, gte, lt, lte                      |\n  | connectionTo           | eq, in                                    |\n  | connectionType         | eq, in                                    |\n  | contentUrl             | eq, in                                    |\n  | createdAt              | eq, gt, gte, lt, lte                      |\n  | databaseName           | eq, in                                    |\n  | databaseUserName       | eq, in                                    |\n  | description            | eq, in                                    |\n  | favoritesTotal         | eq, gt, gte, lt, lte                      |\n  | hasAlert               | eq                                        |\n  | hasEmbeddedPassword    | eq                                        |\n  | hasExtracts            | eq                                        |\n  | isCertified            | eq                                        |\n  | isConnectable          | eq                                        |\n  | isDefaultPort          | eq                                        |\n  | isHierarchical         | eq                                        |\n  | isPublished            | eq                                        |\n  | name                   | eq, in                                    |\n  | ownerDomain            | eq, in                                    |\n  | ownerEmail             | eq                                        |\n  | ownerName              | eq, in                                    |\n  | projectName            | eq, in                                    |\n  | serverName             | eq, in                                    |\n  | serverPort             | eq                                        |\n  | size                   | eq, gt, gte, lt, lte                      |\n  | tableName              | eq, in                                    |\n  | tags                   | eq, in                                    |\n  | type                   | eq                                        |\n  | updatedAt              | eq, gt, gte, lt, lte                      |\n\n  **Supported Operators**\n  - `eq`: equals\n  - `gt`: greater than\n  - `gte`: greater than or equal\n  - `in`: any of [list] (for searching tags)\n  - `lt`: less than\n  - `lte`: less than or equal\n  \n  **Filter Expression Notes**\n  - Filter expressions can\'t contain ampersand (&) or comma (,) characters even if those characters are encoded.\n  - Operators are delimited with colons (:). For example: `filter=name:eq:Project Views`\n  - Field names, operator names, and values are case-sensitive.\n  - To filter on multiple fields, combine expressions using a comma:  `filter=lastLogin:gte:2016-01-01T00:00:00Z,siteRole:eq:Publisher`\n  - Multiple expressions are combined using a logical AND.\n  - If you include the same field multiple times, only the last reference is used.\n  - For date-time values, use ISO 8601 format (e.g., `2016-05-04T21:24:49Z`).\n  - Wildcard searches (starts with, ends with, contains) are supported in recent Tableau versions:\n    - Starts with: `?filter=name:eq:mark*`\n    - Ends with: `?filter=name:eq:*-ample`\n    - Contains: `?filter=name:eq:mark*ex*`\n\n  **Example Usage:**\n  - List data sources with the name "Project Views":\n      filter: "name:eq:Project Views"\n  - List data sources in the "Finance" project:\n      filter: "projectName:eq:Finance"\n  - List data sources created after January 1, 2023:\n      filter: "createdAt:gt:2023-01-01T00:00:00Z"\n  - List data sources with the name "Project Views" in the "Finance" project and created after January 1, 2023:\n      filter: "name:eq:Project Views,projectName:eq:Finance,createdAt:gt:2023-01-01T00:00:00Z"\n\n  **Pagination**\n  This tool returns a single 1000-item page per call. Use `pageNumber` to select which 1-based page to fetch (default 1).\n  The response is a flat object `{ data, totalAvailable }`; to collect every project, keep incrementing `pageNumber` until you have gathered `totalAvailable` items.\n  To get just the count of datasources matching a request, read `totalAvailable` from a single call with `limit: 1` — the count is returned regardless of page size, and a small `limit` keeps the response tiny.\n  ',
    inputSchema: {
      additionalProperties: false,
      properties: {
        filter: {
          type: "string",
        },
        limit: {
          description:
            "The maximum number of data sources to return from the requested page (must be <= 1000). Use this to fetch fewer than a full page, e.g. the final partial page a client wants, or `limit: 1` when you only need the `totalAvailable` count and want to minimize the response payload.",
          exclusiveMinimum: 0,
          maximum: 1000,
          type: "integer",
        },
        pageNumber: {
          description: "Which 1000-item page to fetch (1-based, default 1).",
          exclusiveMinimum: 0,
          type: "integer",
        },
      },
      type: "object",
    },
    name: "list-datasources",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Query Datasource",
    },
    description:
      '# Query Tableau Data Source Tool\n\nExecutes VizQL queries against a Tableau data source (identified by `datasourceLuid`) to answer business questions, retrieving aggregated and filtered data with proper sorting and grouping. The `datasourceLuid` may be a published or an embedded (workbook) data source — including embedded LUIDs from `get-workbook`\'s `upstreamDatasources` (`datasourceType: \'embedded\'`); prefer the embedded copy when both exist, as it carries the workbook\'s local field semantics and calculations. Embedded querying must be enabled on the site — when a LUID comes from `get-workbook`, its `queryability.isQueryable` flag tells you up front whether you can query it (`false` → skip, and `queryability.reason` explains why); otherwise, querying a data source that isn\'t enabled returns a clear "not enabled" error. When querying an embedded data source, also pass `workbookLuid`: the LUID of the workbook it belongs to (the workbook LUID you passed to `get-workbook`). The server configuration may restrict access to embedded data sources based on their workbook.\n\n## Prerequisites\nBefore using this tool, you should:\n1. Understand available fields and their types\n2. Understand what parameters are available and their types\n3. Understand the data structure and field relationships\n\n## Best Practices\n\n### Data Volume Management\n- **Always prefer aggregation** - Use aggregated fields (SUM, COUNT, AVG, etc.) instead of raw row-level data to reduce response size\n- **Profile data before querying** - When unsure about data volume, first run a COUNT query to understand the scale:\n  ```json\n  {\n    "fields": [\n      {\n        "fieldCaption": "Order ID",\n        "function": "COUNT",\n        "fieldAlias": "Total Records"\n      }\n    ]\n  }\n  ```\n- **Use TOP filters for rankings** - When users ask for "top N" results, use TOP filter type to limit results at the database level\n- **Apply restrictive filters** - Use SET, QUANTITATIVE, or DATE filters to reduce data volume before processing\n- **Avoid row-level queries when possible** - Only retrieve individual records when specifically requested and the business need is clear\n\n### Field Usage Guidelines\n- **Prefer existing fields** - Use fields already modeled in the data source rather than creating custom calculations\n- **Use bins for distribution analysis** - Create bins to group continuous data into discrete ranges (e.g., age groups, price ranges)\n- **Validate field availability** - Always check field metadata before constructing queries\n\n### Field Types\n\n#### Dimension Fields\nBasic fields without aggregation:\n```json\n{\n  "fieldCaption": "Category",\n  "fieldAlias": "Product Category"\n}\n```\n\n#### Measure Fields\nFields with aggregation functions (SUM, AVG, COUNT, etc.):\n```json\n{\n  "fieldCaption": "Sales",\n  "function": "SUM",\n  "fieldAlias": "Total Sales",\n  "maxDecimalPlaces": 2\n}\n```\n\n#### Calculated Fields\nCustom fields defined using Tableau calculation syntax:\n```json\n{\n  "fieldCaption": "Profit Margin",\n  "calculation": "SUM([Profit]) / SUM([Sales])",\n  "fieldAlias": "Margin %"\n}\n```\n\n#### Bin Fields\nGroup continuous data into discrete ranges:\n```json\n{\n  "fieldCaption": "Sales",\n  "binSize": 1000,\n  "fieldAlias": "Sales Range"\n}\n```\nThis creates bins of $1,000 intervals (0-1000, 1000-2000, etc.)\n\n### Query Construction\n- **Group by meaningful dimensions** - Ensure grouping supports the business question being asked\n- **Order results logically** - Use sortDirection and sortPriority to present data in a meaningful way\n- **Use appropriate date functions** - Choose the right date aggregation (YEAR, QUARTER, MONTH, WEEK, DAY, or TRUNC_* variants)\n- **Leverage filter capabilities** - Use the extensive filter options to narrow results\n\n## Data Profiling Strategy\n\nWhen a query might return large amounts of data, follow this profiling approach:\n\n**Step 1: Count total records**\n```json\n{\n  "fields": [\n    {\n      "fieldCaption": "Primary_Key_Field",\n      "function": "COUNT",\n      "fieldAlias": "Total Records"\n    }\n  ]\n}\n```\n\n**Step 2: Count by key dimensions**\n```json\n{\n  "fields": [\n    {\n      "fieldCaption": "Category",\n      "fieldAlias": "Category"\n    },\n    {\n      "fieldCaption": "Order ID",\n      "function": "COUNT",\n      "fieldAlias": "Record Count"\n    }\n  ]\n}\n```\n\n**Step 3: Apply appropriate aggregation or filtering based on counts**\n\n## Parameters\n\nParameters are dynamic values defined in the Tableau datasource that can be used to control calculations, filters, and query behavior. They allow for interactive, user-controlled queries without modifying the query structure.\n\n### When to Use Parameters\n- **Dynamic filtering** - Let users control date ranges, regions, or categories\n- **What-if analysis** - Adjust values like growth rates, targets, or thresholds\n- **Calculation control** - Switch between different metrics or calculation methods\n- **User preferences** - Currency selection, display units, or other settings\n\n### Parameter Types\n- **LIST** - Predefined values (e.g., regions, categories)\n- **ANY_VALUE** - Free-form values matching data type\n- **QUANTITATIVE_RANGE** - Numeric values with optional min/max/step\n- **QUANTITATIVE_DATE** - Date values with optional range constraints\n\n### Usage Example\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [...],\n    "filters": [...],\n    "parameters": [\n      {\n        "name": "Selected Year",\n        "value": 2024\n      },\n      {\n        "name": "Currency",\n        "value": "USD"\n      }\n    ]\n  }\n}\n```\n\n- Parameters affect entire query; filters restrict returned data\n\n## Bins\n\nBins group continuous numerical data into discrete ranges, enabling distribution analysis and histogram creation. Unlike parameters and filters, bins are created dynamically in the query.\n\n### Creating Bin Fields\n\nTo create a bin field in a query, you **must** include:\n1. A **measure field** with the base field and aggregation function\n2. A **bin field** with the same fieldCaption and a binSize property\n\n**Example:**\n```json\n{\n  "fields": [\n    {\n      "fieldCaption": "Sales",\n      "function": "SUM",\n      "fieldAlias": "Total Sales"\n    },\n    {\n      "fieldCaption": "Sales",\n      "binSize": 1000,\n      "fieldAlias": "Sales Range"\n    }\n  ]\n}\n```\n\nThis creates bins of $1,000 intervals (0-1000, 1000-2000, etc.)\n\n### When to Use Bins\n- Analyzing distribution patterns (age groups, price ranges, score brackets)\n- Creating histograms or frequency distributions\n- Grouping continuous numerical data into meaningful categories\n- User asks questions like "How many customers in each age range?" or "What\'s the distribution of order sizes?"\n\n### Bin Restrictions\n- **Cannot override existing bin fields** - If a bin field already exists in the datasource, query it without binSize\n- **binSize must be positive** - Only values > 0 are allowed\n- **Measure fields only** - Can only bin numeric/quantitative fields\n- **Choose appropriate bin sizes** - Consider your data range (e.g., bin size 100 for values 0-10,000)\n\n### Querying Existing Bin Fields\n\nIf a bin field already exists in the datasource (e.g., "Profit (bin)"), query it as a regular dimension field:\n\n```json\n{\n  "fields": [\n    {\n      "fieldCaption": "Profit (bin)"\n    },\n    {\n      "fieldCaption": "Profit",\n      "function": "SUM"\n    }\n  ]\n}\n```\n\nTo control existing bin field behavior, use parameters if available (e.g., "Profit Bin Size").\n\n## Filter Types and Usage\n### Filter Context Property\nAll filters support an optional `context` property (boolean) that controls how filters are applied:\n- **`context: true`** - Filter applies to the overall query context (dimension/scope filters)\n- **`context: false`** - Filter applies after context is established (ranking/limiting filters)\n\n**When to use:**\n- Set `context: true` on dimension filters (SET, DATE, QUANTITATIVE) that define the scope of analysis\n- Set `context: false` on TOP filters to rank/limit results within the established context\n- Omit `context` property for simple queries with single filters\n\n**Example: Finding top products within a region:**\n```json\n{\n  "filters": [\n    {\n      "field": { "fieldCaption": "State" },\n      "filterType": "SET",\n      "values": ["California"],\n      "context": true  // Establish California as the context\n    },\n    {\n      "field": { "fieldCaption": "Product Name" },\n      "filterType": "TOP",\n      "howMany": 1,\n      "direction": "TOP",\n      "context": false,  // Find top product within California\n      "fieldToMeasure": { "fieldCaption": "Sales", "function": "SUM" }\n    }\n  ]\n}\n```\n\n### SET Filters\nFilter by specific values:\n```json\n{\n  "field": {"fieldCaption": "Region"},\n  "filterType": "SET",\n  "values": ["North", "South", "East"],\n  "exclude": false\n}\n```\n\n### MATCH Filters\nFilter strings using patterns:\n```json\n{\n  "field": {"fieldCaption": "City"},\n  "filterType": "MATCH",\n  "startsWith": "San"\n}\n```\n\n### TOP Filters\nGet top/bottom N records by a measure:\n```json\n{\n  "field": {"fieldCaption": "Customer Name"},\n  "filterType": "TOP",\n  "howMany": 10,\n  "direction": "TOP",\n  "fieldToMeasure": {"fieldCaption": "Sales", "function": "SUM"}\n}\n```\n\n### QUANTITATIVE Filters\nFilter numeric ranges:\n```json\n{\n  "field": {"fieldCaption": "Sales"},\n  "filterType": "QUANTITATIVE_NUMERICAL",\n  "quantitativeFilterType": "RANGE",\n  "min": 1000,\n  "max": 50000,\n  "includeNulls": false\n}\n```\n\n### DATE Filters\nFilter relative date periods:\n```json\n{\n  "field": {"fieldCaption": "Order Date"},\n  "filterType": "DATE",\n  "periodType": "MONTHS",\n  "dateRangeType": "LAST"\n}\n```\n\n## Limitations\n- **QUANTITATIVE_NUMERICAL min/max operators are inclusive** - For strictly greater-than or less-than logic, use a small offset (for example, min: 10.01 for > 10, or max: 9.99 for < 10).\n- **MATCH filters cannot apply functions to a field** - Unlike all other filters, which can filter on fields with functions applied to them, MATCH filters can only filter by field name or ad hoc calculations.\n\n## Example Queries\n\n### Example 1: Data Profiling Before Large Query\n**Question:** "Show me all customer orders this year"\n\n**Step 1 - Profile the data volume:**\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Order ID",\n        "function": "COUNT",\n        "fieldAlias": "Total Orders This Year"\n      }\n    ],\n    "filters": [\n      {\n        "field": {"fieldCaption": "Order Date"},\n        "filterType": "DATE",\n        "periodType": "YEARS",\n        "dateRangeType": "CURRENT"\n      }\n    ]\n  }\n}\n```\n\n**If count is manageable (< 10,000), proceed with detail query. If large, suggest aggregation:**\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Customer Name"\n      },\n      {\n        "fieldCaption": "Order Date",\n        "function": "TRUNC_MONTH",\n        "sortDirection": "DESC",\n        "sortPriority": 1\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Monthly Sales"\n      }\n    ],\n    "filters": [\n      {\n        "field": {"fieldCaption": "Order Date"},\n        "filterType": "DATE",\n        "periodType": "YEARS",\n        "dateRangeType": "CURRENT"\n      }\n    ]\n  }\n}\n```\n\n### Example 2: Top Customers Query (Using TOP Filter)\n**Question:** "Who are our top 10 customers by revenue?"\n\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Customer Name"\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Total Revenue",\n        "sortDirection": "DESC",\n        "sortPriority": 1\n      }\n    ],\n    "filters": [\n      {\n        "field": {"fieldCaption": "Customer Name"},\n        "filterType": "TOP",\n        "howMany": 10,\n        "direction": "TOP",\n        "fieldToMeasure": {"fieldCaption": "Sales", "function": "SUM"}\n      }\n    ]\n  }\n}\n```\n\n### Example 3: Top N Dimension Query (Using TOP Filter and context property)\n**Question:** "What is the top selling product in California?"\n```json\n{\n  "datasourceLuid": "abc123",\n    "query": {\n    "fields": [\n      {\n        "fieldCaption": "Product Name"\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Total Sales",\n        "sortDirection": "DESC",\n        "sortPriority": 1\n      }\n    ],\n      "filters": [\n        {\n          "field": { "fieldCaption": "State" },\n          "filterType": "SET",\n          "values": ["California"],\n          "context": true\n        },\n        {\n          "field": { "fieldCaption": "Product Name" },\n          "filterType": "TOP",\n          "howMany": 1,\n          "direction": "TOP",\n          "context": false,\n          "fieldToMeasure": { "fieldCaption": "Sales", "function": "SUM" }\n        }\n      ]\n  }\n}\n```\n\n### Example 4: Time Series with Aggregation\n**Question:** "What are our monthly sales trends?"\n\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Order Date",\n        "function": "TRUNC_MONTH",\n        "fieldAlias": "Month",\n        "sortDirection": "ASC",\n        "sortPriority": 1\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Monthly Sales"\n      },\n      {\n        "fieldCaption": "Order ID",\n        "function": "COUNT",\n        "fieldAlias": "Order Count"\n      }\n    ]\n  }\n}\n```\n\n### Example 5: Filtered Category Analysis\n**Question:** "What\'s the performance by product category for high-value orders?"\n\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Category"\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Total Sales"\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "AVG",\n        "fieldAlias": "Average Order Value",\n        "maxDecimalPlaces": 2\n      },\n      {\n        "fieldCaption": "Order ID",\n        "function": "COUNT",\n        "fieldAlias": "Order Count"\n      }\n    ],\n    "filters": [\n      {\n        "field": {"fieldCaption": "Sales"},\n        "filterType": "QUANTITATIVE_NUMERICAL",\n        "quantitativeFilterType": "MIN",\n        "min": 500\n      }\n    ]\n  }\n}\n```\n\n### Example 6: Distribution Analysis Using Bins\n**Question:** "How are our sales distributed across different price ranges?"\n\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Sales",\n        "binSize": 1000,\n        "fieldAlias": "Sales Range",\n        "sortDirection": "ASC",\n        "sortPriority": 1\n      },\n      {\n        "fieldCaption": "Order ID",\n        "function": "COUNT",\n        "fieldAlias": "Number of Orders"\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Total Sales in Range"\n      }\n    ]\n  }\n}\n```\n\n### Example 7: Using Parameters for Dynamic Analysis\n**Question:** "Show me sales for the selected region and year"\n\n```json\n{\n  "datasourceLuid": "abc123",\n  "query": {\n    "fields": [\n      {\n        "fieldCaption": "Category"\n      },\n      {\n        "fieldCaption": "Sales",\n        "function": "SUM",\n        "fieldAlias": "Total Sales",\n        "sortDirection": "DESC",\n        "sortPriority": 1\n      }\n    ],\n    "parameters": [\n      {\n        "name": "Selected Region",\n        "value": "West"\n      },\n      {\n        "name": "Analysis Year",\n        "value": 2024\n      }\n    ]\n  }\n}\n```\n\n## Calculations\n\n**Create calculations when you need to:**\n- Segment data in ways not captured by existing fields\n- Convert data types (e.g., string to date)\n- Aggregate data with custom logic beyond standard functions\n- Filter results based on computed conditions\n- Calculate ratios or derived metrics\n- Perform analysis and the required data is not present in any existing field\n- Transform values during visualization\n- Quickly categorize data into custom groups\n\n**Avoid calculations if you can achieve the same result by:**\n- Applying standard aggregation functions (SUM, AVG, COUNT, etc.) to existing fields\n- Combining existing fields and filters together\n\nNote: Calculated Fields created as part of a query cannot be referenced in other calculations or filters.\n\n## Error Prevention and Data Management\n\n**When to profile data first:**\n- User asks for "all records" or similar broad requests\n- Query involves high-cardinality fields without filters\n- Request could potentially return row-level data for large tables\n\n**Suggest aggregation when:**\n- Profile queries return very high counts (> 10,000 records)\n- User asks questions that can be answered with summaries\n- Performance or response size might be an issue\n\n**Don\'t call this tool if:**\n- The requested fields are not available in the data source\n- The question requires data not present in the current data source\n- Field validation shows incompatible field types for the requested operation\n\n**Instead:**\n- Use metadata tools to understand available fields\n- Suggest alternative questions that can be answered with available data\n- Recommend appropriate aggregation levels for the business question',
    inputSchema: {
      additionalProperties: false,
      properties: {
        datasourceLuid: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
        limit: {
          minimum: 1,
          type: "integer",
        },
        query: {
          additionalProperties: false,
          properties: {
            fields: {
              items: {
                anyOf: [
                  {
                    additionalProperties: false,
                    properties: {
                      fieldAlias: {
                        type: "string",
                      },
                      fieldCaption: {
                        type: "string",
                      },
                      maxDecimalPlaces: {
                        minimum: 0,
                        type: "integer",
                      },
                      sortDirection: {
                        enum: ["ASC", "DESC"],
                        type: "string",
                      },
                      sortPriority: {
                        exclusiveMinimum: 0,
                        type: "integer",
                      },
                    },
                    required: ["fieldCaption"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      fieldAlias: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/fieldAlias",
                      },
                      fieldCaption: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/fieldCaption",
                      },
                      function: {
                        enum: [
                          "SUM",
                          "AVG",
                          "MEDIAN",
                          "COUNT",
                          "COUNTD",
                          "MIN",
                          "MAX",
                          "STDEV",
                          "VAR",
                          "COLLECT",
                          "YEAR",
                          "QUARTER",
                          "MONTH",
                          "WEEK",
                          "DAY",
                          "TRUNC_YEAR",
                          "TRUNC_QUARTER",
                          "TRUNC_MONTH",
                          "TRUNC_WEEK",
                          "TRUNC_DAY",
                          "AGG",
                          "NONE",
                          "UNSPECIFIED",
                        ],
                        type: "string",
                      },
                      maxDecimalPlaces: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/maxDecimalPlaces",
                      },
                      sortDirection: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/sortDirection",
                      },
                      sortPriority: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/sortPriority",
                      },
                    },
                    required: ["fieldCaption", "function"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      calculation: {
                        type: "string",
                      },
                      fieldAlias: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/fieldAlias",
                      },
                      fieldCaption: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/fieldCaption",
                      },
                      maxDecimalPlaces: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/maxDecimalPlaces",
                      },
                      sortDirection: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/sortDirection",
                      },
                      sortPriority: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/sortPriority",
                      },
                    },
                    required: ["fieldCaption", "calculation"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      binSize: {
                        exclusiveMinimum: 0,
                        type: "number",
                      },
                      fieldAlias: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/fieldAlias",
                      },
                      fieldCaption: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/fieldCaption",
                      },
                      maxDecimalPlaces: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/maxDecimalPlaces",
                      },
                      sortDirection: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/sortDirection",
                      },
                      sortPriority: {
                        $ref: "#/properties/query/properties/fields/items/anyOf/0/properties/sortPriority",
                      },
                    },
                    required: ["fieldCaption", "binSize"],
                    type: "object",
                  },
                ],
              },
              type: "array",
            },
            filters: {
              items: {
                anyOf: [
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        type: "boolean",
                      },
                      exclude: {
                        type: "boolean",
                      },
                      field: {
                        anyOf: [
                          {
                            additionalProperties: false,
                            properties: {
                              fieldCaption: {
                                type: "string",
                              },
                            },
                            required: ["fieldCaption"],
                            type: "object",
                          },
                          {
                            additionalProperties: false,
                            properties: {
                              fieldCaption: {
                                type: "string",
                              },
                              function: {
                                $ref: "#/properties/query/properties/fields/items/anyOf/1/properties/function",
                              },
                            },
                            required: ["fieldCaption", "function"],
                            type: "object",
                          },
                          {
                            additionalProperties: false,
                            properties: {
                              calculation: {
                                type: "string",
                              },
                            },
                            required: ["calculation"],
                            type: "object",
                          },
                        ],
                      },
                      filterType: {
                        const: "SET",
                        type: "string",
                      },
                      values: {
                        anyOf: [
                          {
                            items: {
                              type: "string",
                            },
                            type: "array",
                          },
                          {
                            items: {
                              type: "number",
                            },
                            type: "array",
                          },
                          {
                            items: {
                              type: "boolean",
                            },
                            type: "array",
                          },
                        ],
                      },
                    },
                    required: ["field", "filterType", "values"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      direction: {
                        default: "TOP",
                        enum: ["TOP", "BOTTOM"],
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      fieldToMeasure: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        const: "TOP",
                        type: "string",
                      },
                      howMany: {
                        type: "integer",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "howMany",
                      "fieldToMeasure",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      contains: {
                        type: "string",
                      },
                      context: {
                        type: "boolean",
                      },
                      endsWith: {
                        type: "string",
                      },
                      exclude: {
                        type: "boolean",
                      },
                      field: {
                        anyOf: [
                          {
                            $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field/anyOf/0",
                          },
                          {
                            $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field/anyOf/2",
                          },
                        ],
                      },
                      filterType: {
                        const: "MATCH",
                        type: "string",
                      },
                      startsWith: {
                        type: "string",
                      },
                    },
                    required: ["field", "filterType", "startsWith"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      contains: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/contains",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/context",
                      },
                      endsWith: {
                        type: "string",
                      },
                      exclude: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/exclude",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/filterType",
                      },
                      startsWith: {
                        type: "string",
                      },
                    },
                    required: ["field", "filterType", "endsWith"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      contains: {
                        type: "string",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/context",
                      },
                      endsWith: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/endsWith",
                      },
                      exclude: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/exclude",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/2/properties/filterType",
                      },
                      startsWith: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/3/properties/startsWith",
                      },
                    },
                    required: ["field", "filterType", "contains"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        const: "QUANTITATIVE_NUMERICAL",
                        type: "string",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      max: {
                        type: "number",
                      },
                      min: {
                        type: "number",
                      },
                      quantitativeFilterType: {
                        const: "RANGE",
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "quantitativeFilterType",
                      "min",
                      "max",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/5/properties/filterType",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      min: {
                        type: "number",
                      },
                      quantitativeFilterType: {
                        const: "MIN",
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "quantitativeFilterType",
                      "min",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/5/properties/filterType",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      max: {
                        type: "number",
                      },
                      quantitativeFilterType: {
                        const: "MAX",
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "quantitativeFilterType",
                      "max",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/5/properties/filterType",
                      },
                      quantitativeFilterType: {
                        const: "ONLY_NULL",
                        type: "string",
                      },
                    },
                    required: ["field", "filterType", "quantitativeFilterType"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/5/properties/filterType",
                      },
                      quantitativeFilterType: {
                        const: "ONLY_NON_NULL",
                        type: "string",
                      },
                    },
                    required: ["field", "filterType", "quantitativeFilterType"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        const: "QUANTITATIVE_DATE",
                        type: "string",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      maxDate: {
                        type: "string",
                      },
                      minDate: {
                        type: "string",
                      },
                      quantitativeFilterType: {
                        const: "RANGE",
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "quantitativeFilterType",
                      "minDate",
                      "maxDate",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/10/properties/filterType",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      minDate: {
                        type: "string",
                      },
                      quantitativeFilterType: {
                        const: "MIN",
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "quantitativeFilterType",
                      "minDate",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/10/properties/filterType",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      maxDate: {
                        type: "string",
                      },
                      quantitativeFilterType: {
                        const: "MAX",
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "quantitativeFilterType",
                      "maxDate",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/10/properties/filterType",
                      },
                      quantitativeFilterType: {
                        const: "ONLY_NULL",
                        type: "string",
                      },
                    },
                    required: ["field", "filterType", "quantitativeFilterType"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/10/properties/filterType",
                      },
                      quantitativeFilterType: {
                        const: "ONLY_NON_NULL",
                        type: "string",
                      },
                    },
                    required: ["field", "filterType", "quantitativeFilterType"],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      anchorDate: {
                        type: "string",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      dateRangeType: {
                        const: "CURRENT",
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        const: "DATE",
                        type: "string",
                      },
                      includeNulls: {
                        type: "boolean",
                      },
                      periodType: {
                        enum: [
                          "MINUTES",
                          "HOURS",
                          "DAYS",
                          "WEEKS",
                          "MONTHS",
                          "QUARTERS",
                          "YEARS",
                        ],
                        type: "string",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "periodType",
                      "dateRangeType",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      anchorDate: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/anchorDate",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      dateRangeType: {
                        const: "LAST",
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/filterType",
                      },
                      includeNulls: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/includeNulls",
                      },
                      periodType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/periodType",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "periodType",
                      "dateRangeType",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      anchorDate: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/anchorDate",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      dateRangeType: {
                        const: "NEXT",
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/filterType",
                      },
                      includeNulls: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/includeNulls",
                      },
                      periodType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/periodType",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "periodType",
                      "dateRangeType",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      anchorDate: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/anchorDate",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      dateRangeType: {
                        const: "TODATE",
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/filterType",
                      },
                      includeNulls: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/includeNulls",
                      },
                      periodType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/periodType",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "periodType",
                      "dateRangeType",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      anchorDate: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/anchorDate",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      dateRangeType: {
                        const: "LASTN",
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/filterType",
                      },
                      includeNulls: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/includeNulls",
                      },
                      periodType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/periodType",
                      },
                      rangeN: {
                        type: "integer",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "periodType",
                      "dateRangeType",
                      "rangeN",
                    ],
                    type: "object",
                  },
                  {
                    additionalProperties: false,
                    properties: {
                      anchorDate: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/anchorDate",
                      },
                      context: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/context",
                      },
                      dateRangeType: {
                        const: "NEXTN",
                        type: "string",
                      },
                      field: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/0/properties/field",
                      },
                      filterType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/filterType",
                      },
                      includeNulls: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/includeNulls",
                      },
                      periodType: {
                        $ref: "#/properties/query/properties/filters/items/anyOf/15/properties/periodType",
                      },
                      rangeN: {
                        type: "integer",
                      },
                    },
                    required: [
                      "field",
                      "filterType",
                      "periodType",
                      "dateRangeType",
                      "rangeN",
                    ],
                    type: "object",
                  },
                ],
              },
              type: "array",
            },
            parameters: {
              items: {
                additionalProperties: false,
                properties: {
                  parameterCaption: {
                    type: "string",
                  },
                  value: {
                    type: ["number", "string", "boolean", "null"],
                  },
                },
                required: ["parameterCaption", "value"],
                type: "object",
              },
              type: "array",
            },
          },
          required: ["fields"],
          type: "object",
        },
        workbookLuid: {
          $ref: "#/properties/datasourceLuid",
          description:
            "Required to access an embedded (workbook) data source: the LUID of the workbook it belongs to, e.g. the workbook LUID passed to get-workbook. Omit for published data sources.",
        },
      },
      required: ["datasourceLuid", "query"],
      type: "object",
    },
    name: "query-datasource",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List All Pulse Metric Definitions",
    },
    description:
      "\nRetrieves a list of all published Pulse Metric Definitions using the Tableau REST API.  Use this tool when a user requests to list all Tableau Pulse Metric Definitions on the current site.\n\n**Parameters:**\n- `view` (optional): The range of metrics to return for a definition. The default is 'DEFINITION_VIEW_BASIC' if not specified.\n  - `DEFINITION_VIEW_BASIC` - Return only the specified metric definition.\n  - `DEFINITION_VIEW_FULL` - Return the metric definition and the specified number of metrics.\n  - `DEFINITION_VIEW_DEFAULT` - Return the metric definition and the default metric.\n- `limit` (optional): Maximum number of metric definitions to return. If not specified, all definitions are returned.\n- `pageSize` (optional): Number of results per page. Controls how many definitions are fetched in each API request during pagination.\n\n**Example Usage:**\n- List all Pulse Metric Definitions on the current site\n- List all Pulse Metric Definitions on the current site with the default view:\n    view: 'DEFINITION_VIEW_DEFAULT'\n- List the first 50 Pulse Metric Definitions:\n    limit: 50\n- List all Pulse Metric Definitions on the current site with the full view:\n    view: 'DEFINITION_VIEW_FULL'\n    In the response you will only get up to 5 metrics, so if you want to see more you need to retrieve all the Pulse Metrics from another tool.\n- List all Pulse Metric Definitions on the current site with the basic view:\n    view: 'DEFINITION_VIEW_BASIC'\n- See all metrics for my Pulse Metric Definitions:\n    view: 'DEFINITION_VIEW_FULL'\n    In the response you will only get up to 5 metrics, so if you want to see more you need to retrieve all the Pulse Metrics from another tool.\n",
    inputSchema: {
      additionalProperties: false,
      properties: {
        limit: {
          exclusiveMinimum: 0,
          type: "number",
        },
        pageSize: {
          exclusiveMinimum: 0,
          type: "number",
        },
        view: {
          enum: [
            "DEFINITION_VIEW_BASIC",
            "DEFINITION_VIEW_FULL",
            "DEFINITION_VIEW_DEFAULT",
          ],
          type: "string",
        },
      },
      type: "object",
    },
    name: "list-all-pulse-metric-definitions",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Pulse Metric Definitions from Metric Definition IDs",
    },
    description:
      "\nRetrieves a list of specific Pulse Metric Definitions using the Tableau REST API from a list of metric definition IDs.  Use this tool when a user requests information about specific Pulse Metric Definitions on the current site.\n\n**Parameters:**\n- `metricDefinitionIds` (required): A list of metric definition IDs to retrieve.\n- `view` (optional): The range of metrics to return for a definition. The default is 'DEFINITION_VIEW_BASIC' if not specified.\n  - `DEFINITION_VIEW_BASIC` - Return only the specified metric definition.\n  - `DEFINITION_VIEW_FULL` - Return the metric definition and the specified number of metrics.\n  - `DEFINITION_VIEW_DEFAULT` - Return the metric definition and the default metric.\n\n**Example Usage:**\n- Can you show me details about Pulse Metric Definition with id 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C3'\n    metricDefinitionIds: ['BBC908D8-29ED-48AB-A78E-ACF8A424C8C3']\n- List Pulse Metric Definitions from a list of metric definition IDs:\n    metricDefinitionIds: ['BBC908D8-29ED-48AB-A78E-ACF8A424C8C3', 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C4']\n- List these Pulse Metric Definitions with the default view:\n    metricDefinitionIds: ['BBC908D8-29ED-48AB-A78E-ACF8A424C8C3', 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C4'],\n    view: 'DEFINITION_VIEW_DEFAULT'\n- List these Pulse Metric Definitions with the full view:\n    metricDefinitionIds: ['BBC908D8-29ED-48AB-A78E-ACF8A424C8C3', 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C4'],\n    view: 'DEFINITION_VIEW_FULL',\n    In the response you will only get up to 5 metrics, so if you want to see more you need to retrieve all the Pulse Metrics from another tool.\n- List these Pulse Metric Definitions with the basic view:\n    metricDefinitionIds: ['BBC908D8-29ED-48AB-A78E-ACF8A424C8C3', 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C4'],\n    view: 'DEFINITION_VIEW_BASIC'\n- See all metrics for these Pulse Metric Definitions with the full view:\n    metricDefinitionIds: ['BBC908D8-29ED-48AB-A78E-ACF8A424C8C3', 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C4'],\n    view: 'DEFINITION_VIEW_FULL'\n    In the response you will only get up to 5 metrics, so if you want to see more you need to retrieve all the Pulse Metrics from another tool.\n",
    inputSchema: {
      additionalProperties: false,
      properties: {
        metricDefinitionIds: {
          items: {
            pattern:
              "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
            type: "string",
          },
          minItems: 1,
          type: "array",
        },
        view: {
          enum: [
            "DEFINITION_VIEW_BASIC",
            "DEFINITION_VIEW_FULL",
            "DEFINITION_VIEW_DEFAULT",
          ],
          type: "string",
        },
      },
      required: ["metricDefinitionIds"],
      type: "object",
    },
    name: "list-pulse-metric-definitions-from-definition-ids",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Pulse Metrics from Metric Definition ID",
    },
    description:
      "\nRetrieves a list of published Pulse Metrics from a Pulse Metric Definition using the Tableau REST API.  Use this tool when a user requests to list Tableau Pulse Metrics for a specific Pulse Metric Definition on the current site.\n\n**Parameters:**\n- `pulseMetricDefinitionID` (required): The ID of the Pulse Metric Definition to list metrics for.  It should be the ID of the Pulse Metric Definition, not the name.  Example: BBC908D8-29ED-48AB-A78E-ACF8A424C8C3\n\n**Example Usage:**\n- List all Pulse Metrics for this Pulse Metric Definition\n",
    inputSchema: {
      additionalProperties: false,
      properties: {
        pulseMetricDefinitionID: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
      },
      required: ["pulseMetricDefinitionID"],
      type: "object",
    },
    name: "list-pulse-metrics-from-metric-definition-id",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Pulse Metrics from Metric IDs",
    },
    description:
      "\nRetrieves a list of published Pulse Metrics from a list of metric IDs using the Tableau REST API.  Use this tool when a user requests to list Tableau Pulse Metrics for a list of metric IDs on the current site.\n\n**Parameters:**\n- `metricIds` (required): The list of Pulse Metric IDs to list metrics for.  It should be the list of metric IDs, not the names or metric definition ids.  Example: ['CF32DDCC-362B-4869-9487-37DA4D152552', 'CF32DDCC-362B-4869-9487-37DA4D152553']\n   - For data in a Pulse Metric Subscription, use the metric_id field.\n\n**Example Usage:**\n- List all Pulse Metrics from a list of Pulse Metric IDs\n\n**Note:**\n- This tool is recommended for use with data in Pulse Metric Subscriptions.\n- 00000000-0000-0000-0000-000000000000 is not a valid datasource id.\n- If you need a valid datasource id, you may need to retrieve the Pulse Metric Definition for the Pulse Metric which should have a valid datasource information.\n",
    inputSchema: {
      additionalProperties: false,
      properties: {
        metricIds: {
          items: {
            pattern:
              "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
            type: "string",
          },
          type: "array",
        },
      },
      required: ["metricIds"],
      type: "object",
    },
    name: "list-pulse-metrics-from-metric-ids",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Pulse Metric Subscriptions for Current User",
    },
    description:
      "\nRetrieves a list of published Pulse Metric Subscriptions for the current user using the Tableau REST API.  Use this tool when a user requests to list Tableau Pulse Metric Subscriptions for the current user.\n\n**Example Usage:**\n- List all Pulse Metric Subscriptions for the current user on the current site\n- List all of my Pulse Metric Subscriptions\n\n**Note:**\n- This tool does not directly provide information about Pulse Metric Definitions.  If you need to know information about Pulse Metric Defintiions associated with your subscriptions you need to:\n  1. Retrieve Pulse Metrics from the metric ids returned in the Pulse Metric Subscriptions.\n  2. Retrieve Pulse Metric Definitions from the metric definition id returned in the Pulse Metrics.\n",
    inputSchema: {
      properties: {},
      type: "object",
    },
    name: "list-pulse-metric-subscriptions",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Generate Pulse Metric Value Insight Bundle",
    },
    description:
      "\nGenerate an insight bundle for the current aggregated value for Pulse Metric using Tableau REST API.  You need the full information of the Pulse Metric and Pulse Metric Definition to use this tool.\n\n**Parameters:**\n- `bundleRequest` (required): The request to generate a bundle for.  Most of the information comes from data returned from other tools that retrieve Pulse Metric and Pulse Metric Definition information.  When creating the bundleRequest, you will need to set options using the following values:\n    - output_format: 'OUTPUT_FORMAT_HTML'\n    - time_zone: 'UTC'\n    - language: 'LANGUAGE_EN_US'\n    - locale: 'LOCALE_EN_US'\n    - The `datasource` field under `metric.definition` requires an `id` (datasource LUID) and accepts an optional `id_type`:\n      - Omit `id_type` for standard published datasources (default behavior).\n      - Use `'DATASOURCE_ID_TYPE_WORKBOOK_DATASOURCE'` when the metric is based on an embedded workbook datasource rather than a published datasource.\n- `bundleType` (optional): The type of bundle to generate.  The default is 'ban'.\n  - 'ban' - Return a basic insight bundle with the current aggregated value for the Pulse Metric, period over period change, and the highest ranked insight for each filterable dimension of the metric.\n  - 'springboard' - Return a springboard insight bundle with the current value, period over period change, and the highest ranked insight for the metric.\n  - 'basic' - Similar to a springboard insight, but data is focused on the dimensions of a metric that are low bandwidth because they have small value sets. It shows the current value, period over period change, and the highest ranked insight for the metric for that data.\n  - 'detail' - Shows insights on performance over time of the metric, a summary visualization of metric highs and lows and trends, breakdowns of top contributors for each filterable dimension of the metric, and followup insights based on the top ranked insights not already presented.\n\n**Example Usage:**\n- Generate the default insight bundle for the Pulse metric:\n    bundleRequest: {\n      bundle_request: {\n        version: 1,\n        options: {\n          output_format: 'OUTPUT_FORMAT_HTML',\n          time_zone: 'UTC',\n          language: 'LANGUAGE_EN_US',\n          locale: 'LOCALE_EN_US',\n        },\n        input: {\n          metadata: {\n            name: 'Pulse Metric',\n            metric_id: 'CF32DDCC-362B-4869-9487-37DA4D152552',\n            definition_id: 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C3',\n          },\n          metric: {\n            definition: {\n              datasource: {\n                id: 'A6FC3C9F-4F40-4906-8DB0-AC70C5FB5A11',\n              },\n              basic_specification: {\n                measure: {\n                  field: 'Sales',\n                  aggregation: 'AGGREGATION_SUM',\n                },\n                time_dimension: {\n                  field: 'Order Date',\n                },\n                filters: [],\n              },\n              is_running_total: false,\n            },\n            metric_specification: {\n              filters: [],\n              measurement_period: {\n                granularity: 'GRANULARITY_BY_QUARTER',\n                range: 'RANGE_LAST_COMPLETE',\n              },\n              comparison: {\n                comparison: 'TIME_COMPARISON_PREVIOUS_PERIOD',\n              },\n            },\n            extension_options: {\n              allowed_dimensions: [],\n              allowed_granularities: [],\n              offset_from_today: 0,\n            },\n            representation_options: {\n              type: 'NUMBER_FORMAT_TYPE_NUMBER',\n              number_units: {\n                singular_noun: 'unit',\n                plural_noun: 'units',\n              },\n              row_level_id_field: {\n                identifier_col: 'Order ID',\n                identifier_label: '',\n              },\n              row_level_entity_names: {\n                entity_name_singular: 'Order',\n              },\n              row_level_name_field: {\n                name_col: 'Order Name',\n              },\n              currency_code: 'CURRENCY_CODE_USD',\n            },\n            insights_options: {\n              show_insights: true,\n              settings: [],\n            },\n            goals: {\n              target: {\n                value: 100,\n              },\n            },\n          },\n        },\n      },\n    },\n- Generate the ban insight bundle for the Pulse metric:\n    bundleType: 'ban',\n    bundleRequest: (See default example above)\n- Generate the springboard insight bundle for the Pulse metric:\n    bundleType: 'springboard',\n    bundleRequest: (See default example above)\n- Generate the basic insight bundle for the Pulse metric:\n    bundleType: 'basic',\n    bundleRequest: (See default example above)\n- Generate the detail insight bundle for the Pulse metric:\n    bundleType: 'detail',\n    bundleRequest: (See default example above)\n",
    inputSchema: {
      additionalProperties: false,
      properties: {
        bundleRequest: {
          additionalProperties: false,
          properties: {
            bundle_request: {
              additionalProperties: false,
              properties: {
                input: {
                  additionalProperties: false,
                  properties: {
                    metadata: {
                      additionalProperties: false,
                      properties: {
                        definition_id: {
                          type: "string",
                        },
                        metric_id: {
                          type: "string",
                        },
                        name: {
                          type: "string",
                        },
                      },
                      type: "object",
                    },
                    metric: {
                      additionalProperties: false,
                      properties: {
                        definition: {
                          additionalProperties: false,
                          properties: {
                            basic_specification: {
                              additionalProperties: false,
                              properties: {
                                filters: {
                                  items: {
                                    additionalProperties: false,
                                    properties: {
                                      categorical_values: {
                                        items: {
                                          additionalProperties: false,
                                          properties: {
                                            bool_value: {
                                              type: "boolean",
                                            },
                                            null_value: {
                                              type: ["string", "null"],
                                            },
                                            string_value: {
                                              type: "string",
                                            },
                                          },
                                          type: "object",
                                        },
                                        type: "array",
                                      },
                                      field: {
                                        type: "string",
                                      },
                                      operator: {
                                        type: "string",
                                      },
                                    },
                                    required: [
                                      "field",
                                      "operator",
                                      "categorical_values",
                                    ],
                                    type: "object",
                                  },
                                  type: "array",
                                },
                                measure: {
                                  additionalProperties: false,
                                  properties: {
                                    aggregation: {
                                      type: "string",
                                    },
                                    field: {
                                      type: "string",
                                    },
                                  },
                                  required: ["field", "aggregation"],
                                  type: "object",
                                },
                                time_dimension: {
                                  additionalProperties: false,
                                  properties: {
                                    field: {
                                      type: "string",
                                    },
                                  },
                                  required: ["field"],
                                  type: "object",
                                },
                              },
                              required: [
                                "measure",
                                "time_dimension",
                                "filters",
                              ],
                              type: "object",
                            },
                            datasource: {
                              additionalProperties: false,
                              properties: {
                                id: {
                                  type: "string",
                                },
                                id_type: {
                                  enum: [
                                    "DATASOURCE_ID_TYPE_WORKBOOK_DATASOURCE",
                                  ],
                                  type: "string",
                                },
                              },
                              required: ["id"],
                              type: "object",
                            },
                            is_running_total: {
                              type: "boolean",
                            },
                          },
                          required: [
                            "datasource",
                            "basic_specification",
                            "is_running_total",
                          ],
                          type: "object",
                        },
                        extension_options: {
                          additionalProperties: false,
                          properties: {
                            allowed_dimensions: {
                              items: {
                                type: "string",
                              },
                              type: "array",
                            },
                            allowed_granularities: {
                              items: {
                                type: "string",
                              },
                              type: "array",
                            },
                            offset_from_today: {
                              type: "number",
                            },
                          },
                          type: "object",
                        },
                        goals: {
                          additionalProperties: false,
                          properties: {
                            target: {
                              additionalProperties: false,
                              properties: {
                                value: {
                                  type: "number",
                                },
                              },
                              required: ["value"],
                              type: "object",
                            },
                          },
                          type: "object",
                        },
                        insights_options: {
                          additionalProperties: false,
                          properties: {
                            settings: {
                              items: {
                                additionalProperties: false,
                                properties: {
                                  disabled: {
                                    type: "boolean",
                                  },
                                  type: {
                                    type: "string",
                                  },
                                },
                                required: ["type", "disabled"],
                                type: "object",
                              },
                              type: "array",
                            },
                            show_insights: {
                              type: "boolean",
                            },
                          },
                          type: "object",
                        },
                        metric_specification: {
                          additionalProperties: false,
                          properties: {
                            comparison: {
                              additionalProperties: false,
                              properties: {
                                comparison: {
                                  type: "string",
                                },
                              },
                              required: ["comparison"],
                              type: "object",
                            },
                            filters: {
                              items: {
                                $ref: "#/properties/bundleRequest/properties/bundle_request/properties/input/properties/metric/properties/definition/properties/basic_specification/properties/filters/items",
                              },
                              type: "array",
                            },
                            measurement_period: {
                              additionalProperties: false,
                              properties: {
                                granularity: {
                                  type: "string",
                                },
                                range: {
                                  type: "string",
                                },
                              },
                              required: ["granularity", "range"],
                              type: "object",
                            },
                          },
                          required: ["measurement_period", "comparison"],
                          type: "object",
                        },
                        representation_options: {
                          additionalProperties: false,
                          properties: {
                            currency_code: {
                              type: "string",
                            },
                            number_units: {
                              additionalProperties: false,
                              properties: {
                                plural_noun: {
                                  type: "string",
                                },
                                singular_noun: {
                                  type: "string",
                                },
                              },
                              type: "object",
                            },
                            row_level_entity_names: {
                              additionalProperties: false,
                              properties: {
                                entity_name_plural: {
                                  type: "string",
                                },
                                entity_name_singular: {
                                  type: "string",
                                },
                              },
                              type: "object",
                            },
                            row_level_id_field: {
                              additionalProperties: false,
                              properties: {
                                identifier_col: {
                                  type: "string",
                                },
                              },
                              type: "object",
                            },
                            row_level_name_field: {
                              additionalProperties: false,
                              properties: {
                                name_col: {
                                  type: "string",
                                },
                              },
                              type: "object",
                            },
                            sentiment_type: {
                              type: "string",
                            },
                            type: {
                              type: "string",
                            },
                          },
                          required: ["type"],
                          type: "object",
                        },
                      },
                      required: ["definition", "metric_specification"],
                      type: "object",
                    },
                  },
                  required: ["metadata", "metric"],
                  type: "object",
                },
                options: {
                  additionalProperties: false,
                  properties: {
                    language: {
                      enum: [
                        "LANGUAGE_UNSPECIFIED",
                        "LANGUAGE_DE_DE",
                        "LANGUAGE_EN_US",
                        "LANGUAGE_EN_GB",
                        "LANGUAGE_ES_ES",
                        "LANGUAGE_FR_FR",
                        "LANGUAGE_FR_CA",
                        "LANGUAGE_GA_IE",
                        "LANGUAGE_IT_IT",
                        "LANGUAGE_JA_JP",
                        "LANGUAGE_KO_KR",
                        "LANGUAGE_NL_NL",
                        "LANGUAGE_PT_BR",
                        "LANGUAGE_SV_SE",
                        "LANGUAGE_TH_TH",
                        "LANGUAGE_ZH_CN",
                        "LANGUAGE_ZH_TW",
                      ],
                      type: "string",
                    },
                    locale: {
                      enum: [
                        "LOCALE_UNSPECIFIED",
                        "LOCALE_AR_AE",
                        "LOCALE_AR_BH",
                        "LOCALE_AR_DZ",
                        "LOCALE_AR_EG",
                        "LOCALE_AR_IQ",
                        "LOCALE_AR_JO",
                        "LOCALE_AR_KW",
                        "LOCALE_AR_LB",
                        "LOCALE_AR_LY",
                        "LOCALE_AR_MA",
                        "LOCALE_AR_OM",
                        "LOCALE_AR_QA",
                        "LOCALE_AR_SA",
                        "LOCALE_AR_SD",
                        "LOCALE_AR_SY",
                        "LOCALE_AR_TN",
                        "LOCALE_AR_YE",
                        "LOCALE_BE_BY",
                        "LOCALE_BG_BG",
                        "LOCALE_CA_ES",
                        "LOCALE_CS_CZ",
                        "LOCALE_DA_DK",
                        "LOCALE_DE_AT",
                        "LOCALE_DE_CH",
                        "LOCALE_DE_DE",
                        "LOCALE_DE_LU",
                        "LOCALE_EL_CY",
                        "LOCALE_EL_GR",
                        "LOCALE_EN_AU",
                        "LOCALE_EN_CA",
                        "LOCALE_EN_GB",
                        "LOCALE_EN_IE",
                        "LOCALE_EN_IN",
                        "LOCALE_EN_MT",
                        "LOCALE_EN_NZ",
                        "LOCALE_EN_PH",
                        "LOCALE_EN_SG",
                        "LOCALE_EN_US",
                        "LOCALE_EN_ZA",
                        "LOCALE_ES_AR",
                        "LOCALE_ES_BO",
                        "LOCALE_ES_CL",
                        "LOCALE_ES_CO",
                        "LOCALE_ES_CR",
                        "LOCALE_ES_DO",
                        "LOCALE_ES_EC",
                        "LOCALE_ES_ES",
                        "LOCALE_ES_GT",
                        "LOCALE_ES_HN",
                        "LOCALE_ES_MX",
                        "LOCALE_ES_NI",
                        "LOCALE_ES_PA",
                        "LOCALE_ES_PE",
                        "LOCALE_ES_PR",
                        "LOCALE_ES_PY",
                        "LOCALE_ES_SV",
                        "LOCALE_ES_US",
                        "LOCALE_ES_UY",
                        "LOCALE_ES_VE",
                        "LOCALE_ET_EE",
                        "LOCALE_FI_FI",
                        "LOCALE_FR_BE",
                        "LOCALE_FR_CA",
                        "LOCALE_FR_CH",
                        "LOCALE_FR_FR",
                        "LOCALE_FR_LU",
                        "LOCALE_GA_IE",
                        "LOCALE_HE_IL",
                        "LOCALE_HI_IN",
                        "LOCALE_HR_HR",
                        "LOCALE_HU_HU",
                        "LOCALE_ID_ID",
                        "LOCALE_IN_ID",
                        "LOCALE_IS_IS",
                        "LOCALE_IT_CH",
                        "LOCALE_IT_IT",
                        "LOCALE_IW_IL",
                        "LOCALE_JA_JP",
                        "LOCALE_KO_KR",
                        "LOCALE_LT_LT",
                        "LOCALE_LV_LV",
                        "LOCALE_MK_MK",
                        "LOCALE_MS_MY",
                        "LOCALE_MT_MT",
                        "LOCALE_NL_BE",
                        "LOCALE_NL_NL",
                        "LOCALE_NB_NO",
                        "LOCALE_NO_NO",
                        "LOCALE_PL_PL",
                        "LOCALE_PT_BR",
                        "LOCALE_PT_PT",
                        "LOCALE_RO_RO",
                        "LOCALE_RU_RU",
                        "LOCALE_SK_SK",
                        "LOCALE_SL_SI",
                        "LOCALE_SQ_AL",
                        "LOCALE_SR_BA",
                        "LOCALE_SR_ME",
                        "LOCALE_SR_RS",
                        "LOCALE_SV_SE",
                        "LOCALE_TH_TH",
                        "LOCALE_TR_TR",
                        "LOCALE_UK_UA",
                        "LOCALE_VI_VN",
                        "LOCALE_ZH_CN",
                        "LOCALE_ZH_HK",
                        "LOCALE_ZH_SG",
                        "LOCALE_ZH_TW",
                      ],
                      type: "string",
                    },
                    output_format: {
                      enum: [
                        "OUTPUT_FORMAT_UNSPECIFIED",
                        "OUTPUT_FORMAT_HTML",
                        "OUTPUT_FORMAT_TEXT",
                      ],
                      type: "string",
                    },
                    time_zone: {
                      type: "string",
                    },
                  },
                  required: [
                    "output_format",
                    "time_zone",
                    "language",
                    "locale",
                  ],
                  type: "object",
                },
                version: {
                  type: "number",
                },
              },
              required: ["version", "options", "input"],
              type: "object",
            },
          },
          required: ["bundle_request"],
          type: "object",
        },
        bundleType: {
          enum: ["ban", "springboard", "basic", "detail"],
          type: "string",
        },
      },
      required: ["bundleRequest"],
      type: "object",
    },
    name: "generate-pulse-metric-value-insight-bundle",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Generate Pulse Insight Brief",
    },
    description:
      "\nGenerate a concise insight brief for Pulse Metrics using Tableau REST API. This endpoint provides AI-powered conversational insights based on natural language questions about your metrics.\n\n**What is an Insight Brief?**\nAn insight brief is an AI-generated response to questions about Pulse metrics. It provides:\n- Natural language answers to specific questions\n- Contextual summaries based on metric data\n- Action-oriented advice and recommendations\n- Conversational format optimized for chat interfaces\n\n**Insight Brief vs. Other Bundle Types:**\n- **Brief**: AI-powered conversational insights based on natural language questions (this endpoint)\n- **Detail**: Comprehensive analysis with full visualizations and trend breakdowns\n- **Ban**: Current value with period-over-period change and top dimensional insights\n- **Breakdown**: Emphasizes categorical dimension analysis and distributions\n\n**IMPORTANT Details:**\n\n1. **Same Datasource Recommendation**: The API works best when all metrics in `metric_group_context` come from the same datasource,\n   as this allows the backend to apply consistent filters across metrics. While the API may accept metrics from different datasources,\n   it is recommended to group metrics by datasource and make separate API calls per datasource for optimal results.\n\n2. **Complete Metric Data**: The `metric_group_context` must include complete metric data from the metric definition:\n   - `extension_options` with actual `allowed_dimensions` and `allowed_granularities` arrays (not empty)\n   - `representation_options` with correct `sentiment_type`, `currency_code`, and format settings\n   - `insights_options.settings` with all insight types and their enabled/disabled state\n   - Incomplete data will cause API errors even if it passes schema validation\n\n3. **Multi-Turn Conversations**: you can optionally provide a concise summary of the directly relevant conversation history in the `messages` array. This can help the API to generate more accurate and relevant responses. Do not include full conversation history or arrays of prior conversation context.\n   history in the `messages` array:\n   - Add the initial user question with `role: 'ROLE_USER'`\n   - Add the assistant's response with `role: 'ROLE_ASSISTANT'` and `content` containing the previous response text\n   - Add the follow-up question with `role: 'ROLE_USER'`\n   - Without conversation history, follow-up questions may lack context\n\n**Parameters:**\n- `briefRequest` (required): The request to generate a brief for. This includes:\n  - `language`: Language for the response (e.g., 'LANGUAGE_EN_US')\n  - `locale`: Locale for formatting (e.g., 'LOCALE_EN_US')\n  - `messages`: Array of conversation messages containing:\n    - `action_type`: Type of action ('ACTION_TYPE_ANSWER', 'ACTION_TYPE_SUMMARIZE', 'ACTION_TYPE_ADVISE')\n    - `content`: The user's question or prompt (string, natural language)\n    - `role`: Who initiated the request ('ROLE_USER' or 'ROLE_ASSISTANT')\n    - `metric_group_context`: Array of metrics to analyze (metadata + metric specification)\n    - `metric_group_context_resolved`: Whether the metric context has been resolved (boolean)\n  - `now`: Optional current time in 'YYYY-MM-DD HH:MM:SS' or 'YYYY-MM-DD' format (defaults to midnight if time omitted)\n  - `time_zone`: Optional timezone for date/time calculations\n\n**Action Types:**\n- `ACTION_TYPE_ANSWER`: Answer a specific question about the metric\n- `ACTION_TYPE_SUMMARIZE`: Provide a summary of metric insights\n- `ACTION_TYPE_ADVISE`: Give recommendations or advice based on metric data\n\n**Example Usage:**\n- Ask a question about a metric:\n    briefRequest: {\n      language: 'LANGUAGE_EN_US',\n      locale: 'LOCALE_EN_US',\n      messages: [\n        {\n          action_type: 'ACTION_TYPE_ANSWER',\n          content: 'Why did sales increase this month?',\n          role: 'ROLE_USER',\n          metric_group_context: [\n            {\n              metadata: {\n                name: 'Sales',\n                id: 'CF32DDCC-362B-4869-9487-37DA4D152552',\n                definition_id: 'BBC908D8-29ED-48AB-A78E-ACF8A424C8C3',\n              },\n              metric: {\n                definition: { /* metric definition */ },\n                specification: { /* metric specification */ },\n                candidates: [ /* optional array of candidate definitions */ ],\n              },\n            }\n          ],\n          metric_group_context_resolved: true,\n        }\n      ],\n      now: '2025-11-14 15:30:00',\n      time_zone: 'America/Los_Angeles',\n    }\n\n- Get a summary of multiple metrics:\n    briefRequest: {\n      language: 'LANGUAGE_EN_US',\n      locale: 'LOCALE_EN_US',\n      messages: [\n        {\n          action_type: 'ACTION_TYPE_SUMMARIZE',\n          content: 'Summarize the key changes across my metrics',\n          role: 'ROLE_USER',\n          metric_group_context: [\n            { metadata: { /* Sales metric */ }, metric: { /* ... */ } },\n            { metadata: { /* Revenue metric */ }, metric: { /* ... */ } },\n            { metadata: { /* Customers metric */ }, metric: { /* ... */ } },\n          ],\n          metric_group_context_resolved: true,\n        }\n      ],\n    }\n\n- Get advice based on metric performance:\n    briefRequest: {\n      language: 'LANGUAGE_EN_US',\n      locale: 'LOCALE_EN_US',\n      messages: [\n        {\n          action_type: 'ACTION_TYPE_ADVISE',\n          content: 'What should I focus on to improve revenue?',\n          role: 'ROLE_USER',\n          metric_group_context: [\n            { metadata: { /* Revenue metric */ }, metric: { /* ... */ } },\n          ],\n          metric_group_context_resolved: true,\n        }\n      ],\n    }\n\n- Ask a follow-up question (includes conversation history):\n    briefRequest: {\n      language: 'LANGUAGE_EN_US',\n      locale: 'LOCALE_EN_US',\n      messages: [\n        {\n          action_type: 'ACTION_TYPE_SUMMARIZE',\n          content: 'What are the key insights for Sales?',\n          role: 'ROLE_USER',\n          metric_group_context: [ { metadata: { /* ... */ }, metric: { /* ... */ } } ],\n          metric_group_context_resolved: true,\n        },\n        {\n          action_type: 'ACTION_TYPE_SUMMARIZE',\n          content: 'Sales increased 5% with growth in Region A and B...',\n          role: 'ROLE_ASSISTANT',\n          metric_group_context: [ { metadata: { /* ... */ }, metric: { /* ... */ } } ],\n          metric_group_context_resolved: true,\n        },\n        {\n          action_type: 'ACTION_TYPE_ANSWER',\n          content: 'What factors contributed to the increase?',\n          role: 'ROLE_USER',\n          metric_group_context: [ { metadata: { /* ... */ }, metric: { /* ... */ } } ],\n          metric_group_context_resolved: true,\n        }\n      ],\n    }\n\n**Use Cases:**\n- **Conversational analytics** - Natural language Q&A about metrics\n- **Executive briefings** - \"What should I know about my metrics today?\"\n- **Intelligent alerts** - Context-aware notifications with explanations\n- **Multi-metric analysis** - Ask questions across multiple metrics at once\n",
    inputSchema: {
      additionalProperties: false,
      properties: {
        briefRequest: {
          additionalProperties: false,
          properties: {
            language: {
              enum: [
                "LANGUAGE_UNSPECIFIED",
                "LANGUAGE_DE_DE",
                "LANGUAGE_EN_US",
                "LANGUAGE_EN_GB",
                "LANGUAGE_ES_ES",
                "LANGUAGE_FR_FR",
                "LANGUAGE_FR_CA",
                "LANGUAGE_GA_IE",
                "LANGUAGE_IT_IT",
                "LANGUAGE_JA_JP",
                "LANGUAGE_KO_KR",
                "LANGUAGE_NL_NL",
                "LANGUAGE_PT_BR",
                "LANGUAGE_SV_SE",
                "LANGUAGE_TH_TH",
                "LANGUAGE_ZH_CN",
                "LANGUAGE_ZH_TW",
              ],
              type: "string",
            },
            locale: {
              enum: [
                "LOCALE_UNSPECIFIED",
                "LOCALE_AR_AE",
                "LOCALE_AR_BH",
                "LOCALE_AR_DZ",
                "LOCALE_AR_EG",
                "LOCALE_AR_IQ",
                "LOCALE_AR_JO",
                "LOCALE_AR_KW",
                "LOCALE_AR_LB",
                "LOCALE_AR_LY",
                "LOCALE_AR_MA",
                "LOCALE_AR_OM",
                "LOCALE_AR_QA",
                "LOCALE_AR_SA",
                "LOCALE_AR_SD",
                "LOCALE_AR_SY",
                "LOCALE_AR_TN",
                "LOCALE_AR_YE",
                "LOCALE_BE_BY",
                "LOCALE_BG_BG",
                "LOCALE_CA_ES",
                "LOCALE_CS_CZ",
                "LOCALE_DA_DK",
                "LOCALE_DE_AT",
                "LOCALE_DE_CH",
                "LOCALE_DE_DE",
                "LOCALE_DE_LU",
                "LOCALE_EL_CY",
                "LOCALE_EL_GR",
                "LOCALE_EN_AU",
                "LOCALE_EN_CA",
                "LOCALE_EN_GB",
                "LOCALE_EN_IE",
                "LOCALE_EN_IN",
                "LOCALE_EN_MT",
                "LOCALE_EN_NZ",
                "LOCALE_EN_PH",
                "LOCALE_EN_SG",
                "LOCALE_EN_US",
                "LOCALE_EN_ZA",
                "LOCALE_ES_AR",
                "LOCALE_ES_BO",
                "LOCALE_ES_CL",
                "LOCALE_ES_CO",
                "LOCALE_ES_CR",
                "LOCALE_ES_DO",
                "LOCALE_ES_EC",
                "LOCALE_ES_ES",
                "LOCALE_ES_GT",
                "LOCALE_ES_HN",
                "LOCALE_ES_MX",
                "LOCALE_ES_NI",
                "LOCALE_ES_PA",
                "LOCALE_ES_PE",
                "LOCALE_ES_PR",
                "LOCALE_ES_PY",
                "LOCALE_ES_SV",
                "LOCALE_ES_US",
                "LOCALE_ES_UY",
                "LOCALE_ES_VE",
                "LOCALE_ET_EE",
                "LOCALE_FI_FI",
                "LOCALE_FR_BE",
                "LOCALE_FR_CA",
                "LOCALE_FR_CH",
                "LOCALE_FR_FR",
                "LOCALE_FR_LU",
                "LOCALE_GA_IE",
                "LOCALE_HE_IL",
                "LOCALE_HI_IN",
                "LOCALE_HR_HR",
                "LOCALE_HU_HU",
                "LOCALE_ID_ID",
                "LOCALE_IN_ID",
                "LOCALE_IS_IS",
                "LOCALE_IT_CH",
                "LOCALE_IT_IT",
                "LOCALE_IW_IL",
                "LOCALE_JA_JP",
                "LOCALE_KO_KR",
                "LOCALE_LT_LT",
                "LOCALE_LV_LV",
                "LOCALE_MK_MK",
                "LOCALE_MS_MY",
                "LOCALE_MT_MT",
                "LOCALE_NL_BE",
                "LOCALE_NL_NL",
                "LOCALE_NB_NO",
                "LOCALE_NO_NO",
                "LOCALE_PL_PL",
                "LOCALE_PT_BR",
                "LOCALE_PT_PT",
                "LOCALE_RO_RO",
                "LOCALE_RU_RU",
                "LOCALE_SK_SK",
                "LOCALE_SL_SI",
                "LOCALE_SQ_AL",
                "LOCALE_SR_BA",
                "LOCALE_SR_ME",
                "LOCALE_SR_RS",
                "LOCALE_SV_SE",
                "LOCALE_TH_TH",
                "LOCALE_TR_TR",
                "LOCALE_UK_UA",
                "LOCALE_VI_VN",
                "LOCALE_ZH_CN",
                "LOCALE_ZH_HK",
                "LOCALE_ZH_SG",
                "LOCALE_ZH_TW",
              ],
              type: "string",
            },
            messages: {
              items: {
                additionalProperties: false,
                properties: {
                  action_type: {
                    enum: [
                      "ACTION_TYPE_UNDEFINED",
                      "ACTION_TYPE_ANSWER",
                      "ACTION_TYPE_SUMMARIZE",
                      "ACTION_TYPE_ADVISE",
                    ],
                    type: "string",
                  },
                  content: {
                    type: "string",
                  },
                  metric_group_context: {
                    items: {
                      additionalProperties: false,
                      properties: {
                        metadata: {
                          additionalProperties: false,
                          properties: {
                            definition_id: {
                              type: "string",
                            },
                            metric_id: {
                              type: "string",
                            },
                            name: {
                              type: "string",
                            },
                          },
                          required: ["name"],
                          type: "object",
                        },
                        metric: {
                          additionalProperties: false,
                          properties: {
                            candidates: {
                              items: {
                                additionalProperties: false,
                                properties: {
                                  extension_options: {
                                    $ref: "#/properties/briefRequest/properties/messages/items/properties/metric_group_context/items/properties/metric/properties/extension_options",
                                  },
                                  id: {
                                    type: "string",
                                  },
                                  name: {
                                    type: "string",
                                  },
                                  representation_options: {
                                    $ref: "#/properties/briefRequest/properties/messages/items/properties/metric_group_context/items/properties/metric/properties/representation_options",
                                  },
                                  specification: {
                                    $ref: "#/properties/briefRequest/properties/messages/items/properties/metric_group_context/items/properties/metric/properties/definition",
                                  },
                                },
                                required: [
                                  "id",
                                  "name",
                                  "specification",
                                  "extension_options",
                                  "representation_options",
                                ],
                                type: "object",
                              },
                              type: "array",
                            },
                            definition: {
                              additionalProperties: false,
                              properties: {
                                basic_specification: {
                                  additionalProperties: false,
                                  properties: {
                                    filters: {
                                      items: {
                                        additionalProperties: false,
                                        properties: {
                                          categorical_values: {
                                            items: {
                                              additionalProperties: false,
                                              properties: {
                                                bool_value: {
                                                  type: "boolean",
                                                },
                                                null_value: {
                                                  type: ["string", "null"],
                                                },
                                                string_value: {
                                                  type: "string",
                                                },
                                              },
                                              type: "object",
                                            },
                                            type: "array",
                                          },
                                          field: {
                                            type: "string",
                                          },
                                          operator: {
                                            type: "string",
                                          },
                                        },
                                        required: [
                                          "field",
                                          "operator",
                                          "categorical_values",
                                        ],
                                        type: "object",
                                      },
                                      type: "array",
                                    },
                                    measure: {
                                      additionalProperties: false,
                                      properties: {
                                        aggregation: {
                                          type: "string",
                                        },
                                        field: {
                                          type: "string",
                                        },
                                      },
                                      required: ["field", "aggregation"],
                                      type: "object",
                                    },
                                    time_dimension: {
                                      additionalProperties: false,
                                      properties: {
                                        field: {
                                          type: "string",
                                        },
                                      },
                                      required: ["field"],
                                      type: "object",
                                    },
                                  },
                                  required: [
                                    "measure",
                                    "time_dimension",
                                    "filters",
                                  ],
                                  type: "object",
                                },
                                datasource: {
                                  additionalProperties: false,
                                  properties: {
                                    id: {
                                      type: "string",
                                    },
                                  },
                                  required: ["id"],
                                  type: "object",
                                },
                                is_running_total: {
                                  type: "boolean",
                                },
                                viz_state_specification: {
                                  additionalProperties: false,
                                  properties: {
                                    viz_state_string: {
                                      type: "string",
                                    },
                                  },
                                  required: ["viz_state_string"],
                                  type: "object",
                                },
                              },
                              required: ["datasource", "is_running_total"],
                              type: "object",
                            },
                            extension_options: {
                              additionalProperties: false,
                              properties: {
                                allowed_dimensions: {
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                                allowed_granularities: {
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                                offset_from_today: {
                                  type: "number",
                                },
                              },
                              type: "object",
                            },
                            goals: {
                              additionalProperties: false,
                              properties: {
                                datasource_goals: {
                                  items: {
                                    additionalProperties: false,
                                    properties: {
                                      basic_specification: {
                                        $ref: "#/properties/briefRequest/properties/messages/items/properties/metric_group_context/items/properties/metric/properties/definition/properties/basic_specification",
                                      },
                                      benchmark_sentiment_type: {
                                        type: "string",
                                      },
                                      minimum_granularity: {
                                        type: "string",
                                      },
                                      name: {
                                        type: "string",
                                      },
                                      threshold_basic_specification: {
                                        $ref: "#/properties/briefRequest/properties/messages/items/properties/metric_group_context/items/properties/metric/properties/definition/properties/basic_specification",
                                      },
                                      threshold_viz_state_specification: {
                                        additionalProperties: false,
                                        properties: {
                                          viz_state_string: {
                                            type: "string",
                                          },
                                        },
                                        required: ["viz_state_string"],
                                        type: "object",
                                      },
                                      viz_state_specification: {
                                        additionalProperties: false,
                                        properties: {
                                          viz_state_string: {
                                            type: "string",
                                          },
                                        },
                                        required: ["viz_state_string"],
                                        type: "object",
                                      },
                                    },
                                    required: [
                                      "minimum_granularity",
                                      "benchmark_sentiment_type",
                                      "name",
                                    ],
                                    type: "object",
                                  },
                                  type: "array",
                                },
                                metric_goals: {
                                  additionalProperties: false,
                                  properties: {
                                    target: {
                                      additionalProperties: false,
                                      properties: {
                                        value: {
                                          type: "number",
                                        },
                                      },
                                      required: ["value"],
                                      type: "object",
                                    },
                                  },
                                  type: "object",
                                },
                              },
                              type: "object",
                            },
                            insights_options: {
                              additionalProperties: false,
                              properties: {
                                settings: {
                                  items: {
                                    additionalProperties: false,
                                    properties: {
                                      disabled: {
                                        type: "boolean",
                                      },
                                      type: {
                                        type: "string",
                                      },
                                    },
                                    required: ["type", "disabled"],
                                    type: "object",
                                  },
                                  type: "array",
                                },
                                show_insights: {
                                  type: "boolean",
                                },
                              },
                              type: "object",
                            },
                            metric_specification: {
                              additionalProperties: false,
                              properties: {
                                comparison: {
                                  additionalProperties: false,
                                  properties: {
                                    comparison: {
                                      type: "string",
                                    },
                                  },
                                  required: ["comparison"],
                                  type: "object",
                                },
                                filters: {
                                  items: {
                                    $ref: "#/properties/briefRequest/properties/messages/items/properties/metric_group_context/items/properties/metric/properties/definition/properties/basic_specification/properties/filters/items",
                                  },
                                  type: "array",
                                },
                                measurement_period: {
                                  additionalProperties: false,
                                  properties: {
                                    granularity: {
                                      type: "string",
                                    },
                                    range: {
                                      type: "string",
                                    },
                                  },
                                  required: ["granularity", "range"],
                                  type: "object",
                                },
                              },
                              required: ["measurement_period", "comparison"],
                              type: "object",
                            },
                            representation_options: {
                              additionalProperties: false,
                              properties: {
                                currency_code: {
                                  type: "string",
                                },
                                number_units: {
                                  additionalProperties: false,
                                  properties: {
                                    plural_noun: {
                                      type: "string",
                                    },
                                    singular_noun: {
                                      type: "string",
                                    },
                                  },
                                  type: "object",
                                },
                                row_level_entity_names: {
                                  additionalProperties: false,
                                  properties: {
                                    entity_name_plural: {
                                      type: "string",
                                    },
                                    entity_name_singular: {
                                      type: "string",
                                    },
                                  },
                                  type: "object",
                                },
                                row_level_id_field: {
                                  additionalProperties: false,
                                  properties: {
                                    identifier_col: {
                                      type: "string",
                                    },
                                  },
                                  type: "object",
                                },
                                row_level_name_field: {
                                  additionalProperties: false,
                                  properties: {
                                    name_col: {
                                      type: "string",
                                    },
                                  },
                                  type: "object",
                                },
                                sentiment_type: {
                                  type: "string",
                                },
                                type: {
                                  type: "string",
                                },
                              },
                              required: ["type"],
                              type: "object",
                            },
                          },
                          required: ["definition", "metric_specification"],
                          type: "object",
                        },
                      },
                      required: ["metadata", "metric"],
                      type: "object",
                    },
                    type: "array",
                  },
                  metric_group_context_resolved: {
                    type: "boolean",
                  },
                  role: {
                    enum: ["ROLE_UNDEFINED", "ROLE_USER", "ROLE_ASSISTANT"],
                    type: "string",
                  },
                },
                required: [
                  "action_type",
                  "content",
                  "metric_group_context",
                  "metric_group_context_resolved",
                  "role",
                ],
                type: "object",
              },
              type: "array",
            },
            now: {
              pattern: "^(\\d{4}-\\d{2}-\\d{2}( \\d{2}:\\d{2}:\\d{2})?)?$",
              type: "string",
            },
            time_zone: {
              type: "string",
            },
          },
          required: ["language", "locale", "messages"],
          type: "object",
        },
      },
      required: ["briefRequest"],
      type: "object",
    },
    name: "generate-pulse-insight-brief",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Download Workbook",
    },
    description:
      'Downloads workbook content as a TWB (application/xml) or TWBX (application/octet-stream) file. The returned file metadata includes Tableau-provided content type and filename when available. If the user asks to open the workbook file locally, derive the full Tableau Desktop application path instead of assuming it is named exactly "Tableau Desktop". If multiple Tableau Desktop versions are installed, select the most recent version by comparing version numbers in the application filename/path.',
    inputSchema: {
      additionalProperties: false,
      properties: {
        includeExtract: {
          description:
            "Whether to include workbook extracts in the returned workbook package.",
          type: "boolean",
        },
        workbookId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
      },
      required: ["workbookId"],
      type: "object",
    },
    name: "download-workbook",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Workbook",
    },
    description:
      "Retrieves information about the specified workbook, including information about the views contained in the workbook and backing datasources. The response's upstreamDatasources list each data source the workbook depends on; an entry's queryability.isQueryable is true when the calling user can query that data source with the query-datasource tool and false when they cannot, in which case queryability.reason explains why. The queryability object is omitted entirely when queryability could not be determined. When a data source is not queryable, its owner (or publishedParent.owner) identifies who to contact to request access.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        workbookId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
      },
      required: ["workbookId"],
      type: "object",
    },
    name: "get-workbook",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: true,
      readOnlyHint: false,
      title: "Request Workbook Upload",
    },
    description:
      "Creates a short-lived staged upload URL for a Tableau TWB or TWBX workbook. Upload the workbook bytes to the returned URL, then call publish-workbook with the returned workbookUploadId.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        fileName: {
          description:
            "Name of the Tableau workbook file to upload. Must end in .twb or .twbx.",
          minLength: 1,
          type: "string",
        },
      },
      required: ["fileName"],
      type: "object",
    },
    name: "request-workbook-upload",
  },
  {
    annotations: {
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: true,
      readOnlyHint: false,
      title: "Publish Workbook",
    },
    description:
      "Publishes a TWB or TWBX workbook from a local file path or staged upload id to Tableau. Provide projectId to choose the target project (use list-projects to discover IDs). TWB workbooks are validated up front and uploaded only when validation succeeds, with any blocking errors returned instead of publishing. TWBX workbooks are uploaded directly and validated by Tableau as part of publishing, since Tableau cannot pre-validate extracts packaged inside a TWBX.\n\nProject publishes also return the workbook permission rules (configured rules, not effective access) in permissions, or permissionsNote if that optional read fails.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        name: {
          description: "The name to give the published workbook.",
          minLength: 1,
          type: "string",
        },
        overwrite: {
          default: false,
          description:
            "Whether to overwrite an existing workbook with the same name in the selected destination. Defaults to false.",
          type: "boolean",
        },
        projectId: {
          description:
            "The Tableau project LUID to publish the workbook into. Use list-projects to discover available project IDs.",
          minLength: 1,
          type: "string",
        },
        workbookFilePath: {
          description:
            "Path to a local TWB or TWBX workbook file on the MCP server filesystem. Only supported when staged S3 uploads are not configured.",
          minLength: 1,
          type: "string",
        },
        workbookUploadId: {
          description:
            "Staged workbook upload id returned by request-workbook-upload. Use this for hosted clients that cannot pass a local path.",
          minLength: 1,
          type: "string",
        },
      },
      required: ["name", "projectId"],
      type: "object",
    },
    name: "publish-workbook",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get View",
    },
    description:
      "Retrieves information about the specified view, including upstream datasources, workbook information, project details, owner, tags, and usage statistics. Returns facts only, NO visual output: to display the view use render-interactive-viz (interactive) or get-view-image (static image).",
    inputSchema: {
      additionalProperties: false,
      properties: {
        viewId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
      },
      required: ["viewId"],
      type: "object",
    },
    name: "get-view",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get View Data",
    },
    description:
      "Retrieves data for the specified view in a Tableau workbook, including the user's filters. On Tableau REST API versions below 3.30, returns CSV data for the dashboard's first view. On version 3.30 or later, returns parsed data for the sheets included in the server response. Requires the view LUID from the content URL (not the published view id). For custom views, use the tool to get custom view data by custom view id instead.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        viewFilters: {
          additionalProperties: {
            type: "string",
          },
          description: "Optional map of view filter field names to values.",
          type: "object",
        },
        viewId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
      },
      required: ["viewId"],
      type: "object",
    },
    name: "get-view-data",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get View Image",
    },
    description:
      'Returns a static, non-interactive image of the specified view in a Tableau workbook. Use only when the user explicitly wants an image artifact — a screenshot, picture, thumbnail, PNG/PDF, or an image to embed in a document or export. For a bare "show me / open / explore this view" the user wants the interactive embed — use render-interactive-viz instead. Optional width and height in pixels control render size. Optional view field names and values can be provided to filter the view. For custom views, use the tool to get custom view image by custom view id instead.',
    inputSchema: {
      additionalProperties: false,
      properties: {
        format: {
          description:
            'The image format to return. Use "PNG" (default) when the image will be analyzed or interpreted. Use "SVG" when the image will be displayed to the user — SVG is scalable and produces smaller file sizes.',
          enum: ["PNG", "SVG"],
          type: "string",
        },
        height: {
          exclusiveMinimum: 0,
          type: "integer",
        },
        viewFilters: {
          additionalProperties: {
            type: "string",
          },
          description: "Optional map of view filter field names to values.",
          type: "object",
        },
        viewId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
        width: {
          exclusiveMinimum: 0,
          type: "integer",
        },
      },
      required: ["viewId"],
      type: "object",
    },
    name: "get-view-image",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Workbooks",
    },
    description:
      '\n  Retrieves a list of workbooks on a Tableau site including their metadata such as name, description, and information about the views contained in the workbook. Supports optional filtering via field:operator:value expressions (e.g., name:eq:Superstore) for precise and flexible workbook discovery.\n  To list results based on usage popularity or relevance, use the search-content tool.\n\n  **Supported Filter Fields and Operators**\n  | Field             | Operators            |\n  |-------------------|----------------------|\n  | createdAt         | eq, gt, gte, lt, lte |\n  | contentUrl        | eq, in               |\n  | displayTabs       | eq                   |\n  | favoritesTotal    | eq, gt, gte, lt, lte |\n  | hasAlerts         | eq                   |\n  | hasExtracts       | eq                   |\n  | name              | eq, in               |\n  | ownerDomain       | eq, in               |\n  | ownerEmail        | eq, in               |\n  | ownerName         | eq, in               |\n  | projectName       | eq, in               |\n  | sheetCount        | eq, gt, gte, lt, lte |\n  | size              | eq, gt, gte, lt, lte |\n  | subscriptionTotal | eq, gt, gte, lt, lte |\n  | tags              | eq, in               |\n  | updatedAt         | eq, gt, gte, lt, lte |\n\n  **Supported Operators**\n  - `eq`: equals\n  - `gt`: greater than\n  - `gte`: greater than or equal\n  - `in`: any of [list] (for searching tags)\n  - `lt`: less than\n  - `lte`: less than or equal\n  \n  **Filter Expression Notes**\n  - Filter expressions can\'t contain ampersand (&) or comma (,) characters even if those characters are encoded.\n  - Operators are delimited with colons (:). For example: `filter=name:eq:Project Views`\n  - Field names, operator names, and values are case-sensitive.\n  - To filter on multiple fields, combine expressions using a comma:  `filter=lastLogin:gte:2016-01-01T00:00:00Z,siteRole:eq:Publisher`\n  - Multiple expressions are combined using a logical AND.\n  - If you include the same field multiple times, only the last reference is used.\n  - For date-time values, use ISO 8601 format (e.g., `2016-05-04T21:24:49Z`).\n  - Wildcard searches (starts with, ends with, contains) are supported in recent Tableau versions:\n    - Starts with: `?filter=name:eq:mark*`\n    - Ends with: `?filter=name:eq:*-ample`\n    - Contains: `?filter=name:eq:mark*ex*`\n\n  **Example Usage:**\n  - List workbooks with the name "Superstore":\n      filter: "name:eq:Superstore"\n  - List workbooks in the "Finance" project:\n      filter: "projectName:eq:Finance"\n  - List workbooks created after January 1, 2023:\n      filter: "createdAt:gt:2023-01-01T00:00:00Z"\n  - List workbooks with the name "Superstore" in the "Finance" project and created after January 1, 2023:\n      filter: "name:eq:Superstore,projectName:eq:Finance,createdAt:gt:2023-01-01T00:00:00Z"\n      \n  **Pagination**\n  This tool returns a single 1000-item page per call. Use `pageNumber` to select which 1000-item page to fetch (1-based, default 1).\n  The response is a flat object `{ data, totalAvailable }`; paginate by incrementing `pageNumber` until you have collected `totalAvailable` items.\n  To get just the count of workbooks matching a request, read `totalAvailable` from a single call with `limit: 1` — the count is returned regardless of page size, and a small `limit` keeps the response tiny.',
    inputSchema: {
      additionalProperties: false,
      properties: {
        filter: {
          type: "string",
        },
        limit: {
          description:
            "The maximum number of workbooks to return from the requested page (must be <= 1000). Use this to fetch fewer than a full page, e.g. the final partial page a client wants, or `limit: 1` when you only need the `totalAvailable` count and want to minimize the response payload.",
          exclusiveMinimum: 0,
          maximum: 1000,
          type: "integer",
        },
        pageNumber: {
          description: "Which 1000-item page to fetch (1-based, default 1).",
          exclusiveMinimum: 0,
          type: "integer",
        },
      },
      type: "object",
    },
    name: "list-workbooks",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Projects",
    },
    description:
      '\n  Retrieves a list of projects on a Tableau site including their metadata such as name, description, parent project, content permissions, owner, and timestamps. Supports optional filtering via field:operator:value expressions (e.g., name:eq:Default) for precise project discovery.\n  To list results based on usage popularity or relevance, use the search-content tool instead.\n\n  **Supported Filter Fields and Operators**\n  | Field             | Operators            |\n  |-------------------|----------------------|\n  | createdAt         | eq, gt, gte, lt, lte |\n  | name              | eq, in               |\n  | ownerDomain       | eq, in               |\n  | ownerEmail        | eq, in               |\n  | ownerName         | eq, in               |\n  | parentProjectId   | eq, in               |\n  | topLevelProject   | eq                   |\n  | updatedAt         | eq, gt, gte, lt, lte |\n\n  **Supported Operators**\n  - `eq`: equals\n  - `gt`: greater than\n  - `gte`: greater than or equal\n  - `in`: any of [list] (for searching tags)\n  - `lt`: less than\n  - `lte`: less than or equal\n  \n  **Filter Expression Notes**\n  - Filter expressions can\'t contain ampersand (&) or comma (,) characters even if those characters are encoded.\n  - Operators are delimited with colons (:). For example: `filter=name:eq:Project Views`\n  - Field names, operator names, and values are case-sensitive.\n  - To filter on multiple fields, combine expressions using a comma:  `filter=lastLogin:gte:2016-01-01T00:00:00Z,siteRole:eq:Publisher`\n  - Multiple expressions are combined using a logical AND.\n  - If you include the same field multiple times, only the last reference is used.\n  - For date-time values, use ISO 8601 format (e.g., `2016-05-04T21:24:49Z`).\n  - Wildcard searches (starts with, ends with, contains) are supported in recent Tableau versions:\n    - Starts with: `?filter=name:eq:mark*`\n    - Ends with: `?filter=name:eq:*-ample`\n    - Contains: `?filter=name:eq:mark*ex*`\n\n  **Example Usage:**\n  - List projects with the name "Default":\n      filter: "name:eq:Default"\n  - List top-level projects only:\n      filter: "topLevelProject:eq:true"\n  - List child projects of a specific parent:\n      filter: "parentProjectId:eq:abc-123"\n  - List projects updated after January 1, 2023:\n      filter: "updatedAt:gt:2023-01-01T00:00:00Z"\n\n  **Finding projects to publish to**\n  Pass `capability: "Write"` to return only the projects the current user can publish or create content into. The server applies this filter, so `totalAvailable` counts only those projects. Sites where Tableau has not yet enabled the filter ignore it and return every viewable project, so a publish to a returned project can still fail with a permission error. Use it whenever you ask the user where to publish, so they are only offered projects they can actually publish to. It combines with `filter`, e.g. `capability: "Write"` with `filter: "parentProjectId:eq:abc-123"` lists the publishable child projects of a parent.\n\n  **Pagination**\n  This tool returns a single 1000-item page per call. Use `pageNumber` to select which 1-based page to fetch (default 1).\n  The response is a flat object `{ data, totalAvailable }`; to collect every project, keep incrementing `pageNumber` until you have gathered `totalAvailable` items.\n  To get just the count of projects matching a request, read `totalAvailable` from a single call with `limit: 1` — the count is returned regardless of page size, and a small `limit` keeps the response tiny.',
    inputSchema: {
      additionalProperties: false,
      properties: {
        capability: {
          description:
            "Only return projects the current user has this capability on. Use `Write` to list just the projects the user can publish or create content into, e.g. when asking the user where to publish.",
          enum: ["Write"],
          type: "string",
        },
        filter: {
          type: "string",
        },
        limit: {
          description:
            "The maximum number of projects to return from the requested page (must be <= 1000). Use this to fetch fewer than a full page, e.g. the final partial page a client wants, or `limit: 1` when you only need the `totalAvailable` count and want to minimize the response payload.",
          exclusiveMinimum: 0,
          maximum: 1000,
          type: "integer",
        },
        pageNumber: {
          description: "Which 1000-item page to fetch (1-based, default 1).",
          exclusiveMinimum: 0,
          type: "integer",
        },
      },
      type: "object",
    },
    name: "list-projects",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Views",
    },
    description:
      '\n  Retrieves a list of views on a Tableau site including their metadata such as name, owner, and the workbook they are found in. Supports optional filtering via field:operator:value expressions (e.g., name:eq:Overview) for precise and flexible view discovery.\n  To list results based on usage popularity or relevance, use the search-content tool instead.\n\n  **Supported Filter Fields and Operators**\n  | Field               | Operators            |\n  |---------------------|----------------------|\n  | caption             | eq, in               |\n  | contentUrl          | eq, in               |\n  | createdAt           | eq, gt, gte, lt, lte |\n  | favoritesTotal      | eq, gt, gte, lt, lte |\n  | fields              | eq, in               |\n  | hitsTotal           | eq, gt, gte, lt, lte |\n  | name                | eq, in               |\n  | ownerDomain         | eq, in               |\n  | ownerEmail          | eq, in               |\n  | ownerName           | eq, in               |\n  | projectName         | eq, in               |\n  | sheetNumber         | eq, gt, gte, lt, lte |\n  | sheetType           | eq, in               |\n  | tags                | eq, in               |\n  | title               | eq, in               |\n  | updatedAt           | eq, gt, gte, lt, lte |\n  | viewUrlname         | eq, in               |\n  | workbookDescription | eq, in               |\n  | workbookName        | eq, in               |\n\n  **Supported Operators**\n  - `eq`: equals\n  - `gt`: greater than\n  - `gte`: greater than or equal\n  - `in`: any of [list] (for searching tags)\n  - `lt`: less than\n  - `lte`: less than or equal\n  \n  **Filter Expression Notes**\n  - Filter expressions can\'t contain ampersand (&) or comma (,) characters even if those characters are encoded.\n  - Operators are delimited with colons (:). For example: `filter=name:eq:Project Views`\n  - Field names, operator names, and values are case-sensitive.\n  - To filter on multiple fields, combine expressions using a comma:  `filter=lastLogin:gte:2016-01-01T00:00:00Z,siteRole:eq:Publisher`\n  - Multiple expressions are combined using a logical AND.\n  - If you include the same field multiple times, only the last reference is used.\n  - For date-time values, use ISO 8601 format (e.g., `2016-05-04T21:24:49Z`).\n  - Wildcard searches (starts with, ends with, contains) are supported in recent Tableau versions:\n    - Starts with: `?filter=name:eq:mark*`\n    - Ends with: `?filter=name:eq:*-ample`\n    - Contains: `?filter=name:eq:mark*ex*`\n\n  **Example Usage:**\n  - List views with the name "Overview":\n      filter: "name:eq:Overview"\n  - List views in the "Finance" project:\n      filter: "projectName:eq:Finance"\n  - List views created after January 1, 2023:\n      filter: "createdAt:gt:2023-01-01T00:00:00Z"\n  - List views with the name "Overview" in the "Finance" project and created after January 1, 2023:\n      filter: "name:eq:Overview,projectName:eq:Finance,createdAt:gt:2023-01-01T00:00:00Z"\n\n  **Pagination**\n  This tool returns a single 1000-item page per call. Use `pageNumber` to select which 1000-item page to fetch (1-based, default 1).\n  The response is a flat object `{ data, totalAvailable }`; paginate by incrementing `pageNumber` until you have collected `totalAvailable` items.\n  To get just the count of views matching a request, read `totalAvailable` from a single call with `limit: 1` — the count is returned regardless of page size, and a small `limit` keeps the response tiny.',
    inputSchema: {
      additionalProperties: false,
      properties: {
        filter: {
          type: "string",
        },
        limit: {
          description:
            "The maximum number of views to return from the requested page (must be <= 1000). Use this to fetch fewer than a full page, e.g. the final partial page a client wants, or `limit: 1` when you only need the `totalAvailable` count and want to minimize the response payload.",
          exclusiveMinimum: 0,
          maximum: 1000,
          type: "integer",
        },
        pageNumber: {
          description: "Which 1000-item page to fetch (1-based, default 1).",
          exclusiveMinimum: 0,
          type: "integer",
        },
      },
      type: "object",
    },
    name: "list-views",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Custom Views",
    },
    description:
      '\n  Retrieves a list of custom views for a Tableau workbook including their metadata such as name, owner, and the view they are found in. Supports optional filtering via field:operator:value expressions (e.g., viewId:eq:<view_id>) for precise and flexible custom view discovery. The tool always includes the workbookId in the final filter expression based on the required workbookId argument. Including the workbookId field in the filter will be ignored. Use this tool when a user requests to list, search, or filter Tableau custom views for a workbook.\n\n  **Supported Filter Fields and Operators**\n  | Field               | Operators            |\n  |---------------------|----------------------|\n  | ownerId             | eq                   |\n  | viewId              | eq                   |\n\n  **Supported Operators**\n  - `eq`: equals\n  - `gt`: greater than\n  - `gte`: greater than or equal\n  - `in`: any of [list] (for searching tags)\n  - `lt`: less than\n  - `lte`: less than or equal\n  \n  **Filter Expression Notes**\n  - Filter expressions can\'t contain ampersand (&) or comma (,) characters even if those characters are encoded.\n  - Operators are delimited with colons (:). For example: `filter=name:eq:Project Views`\n  - Field names, operator names, and values are case-sensitive.\n  - To filter on multiple fields, combine expressions using a comma:  `filter=lastLogin:gte:2016-01-01T00:00:00Z,siteRole:eq:Publisher`\n  - Multiple expressions are combined using a logical AND.\n  - If you include the same field multiple times, only the last reference is used.\n  - For date-time values, use ISO 8601 format (e.g., `2016-05-04T21:24:49Z`).\n  - Wildcard searches (starts with, ends with, contains) are supported in recent Tableau versions:\n    - Starts with: `?filter=name:eq:mark*`\n    - Ends with: `?filter=name:eq:*-ample`\n    - Contains: `?filter=name:eq:mark*ex*`\n\n  **Example Usage:**\n  - List all custom views for a given workbook:\n      workbookId: "222ea993-9391-4910-a167-56b3d19b4e3b"\n  - List custom views from the view with viewId "9460abfe-a6b2-49d1-b998-39e1ebcc55ce":\n      workbookId: "222ea993-9391-4910-a167-56b3d19b4e3b"\n      filter: "viewId:eq:9460abfe-a6b2-49d1-b998-39e1ebcc55ce"\n  - List custom views for the owner with ownerId "bbdee366-4a50-4c2c-a5c8-746da5b64483":\n      workbookId: "222ea993-9391-4910-a167-56b3d19b4e3b"\n      filter: "ownerId:eq:bbdee366-4a50-4c2c-a5c8-746da5b64483"',
    inputSchema: {
      additionalProperties: false,
      properties: {
        filter: {
          type: "string",
        },
        limit: {
          exclusiveMinimum: 0,
          maximum: 1000,
          type: "integer",
        },
        workbookId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
      },
      required: ["workbookId"],
      type: "object",
    },
    name: "list-custom-views",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Custom View Data",
    },
    description:
      "Retrieves comma-separated value (CSV) data for a Tableau Custom View (saved/personalized view state), including the user's filters. Requires the custom view LUID from the content URL (not the published view id). For published views, use the tool to get view data by view id instead.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        customViewId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
        viewFilters: {
          additionalProperties: {
            type: "string",
          },
          description: "Optional map of view filter field names to values.",
          type: "object",
        },
      },
      required: ["customViewId"],
      type: "object",
    },
    name: "get-custom-view-data",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Custom View Image",
    },
    description:
      "Retrieves an image of the specified custom view in a published viz. A custom view is a shortcut to a specific state of interaction, such as filter selections and sorting, for a published viz. Requires the custom view LUID from the content URL (not the published view id). Optional width and height in pixels control render size. Optional view field names and values can be provided to filter the custom view. For published views, use the tool to get view image by view id instead.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        customViewId: {
          pattern:
            "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
          type: "string",
        },
        format: {
          description:
            'The image format to return. Use "PNG" (default) when the image will be analyzed or interpreted. Use "SVG" when the image will be displayed to the user — SVG is scalable and produces smaller file sizes.',
          enum: ["PNG", "SVG"],
          type: "string",
        },
        height: {
          exclusiveMinimum: 0,
          type: "integer",
        },
        viewFilters: {
          additionalProperties: {
            type: "string",
          },
          description: "Optional map of view filter field names to values.",
          type: "object",
        },
        width: {
          exclusiveMinimum: 0,
          type: "integer",
        },
      },
      required: ["customViewId"],
      type: "object",
    },
    name: "get-custom-view-image",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Search Content",
    },
    description:
      '\nThis tool searches and ranks Tableau content across many content types at once — including workbooks, views, datasources, projects, lenses, flows, tables, databases, virtual connections, data roles, and collections.\nUse this tool for keyword or free-text discovery: when you want to find content by name or topic, when you do not know which content type an item is, when you want to search several content types in a single call, or when you want the most relevant or most-viewed items surfaced first.\nIt returns a single ranked page (the top N matches — default 100, max 2000) rather than an exhaustive enumeration of every match, so it is best suited to finding the most relevant items rather than returning every matching item.\n\n**Parameters:**\n\n- `terms` (optional): A string containing one or more search terms that the search uses as the basis for determining which items are relevant to return.\n\n- `filter` (optional): Allows you to limit search results based on:\n  - `contentTypes`: Filter by content types. Supported types are: \'lens\', \'datasource\', \'virtualconnection\', \'collection\', \'project\', \'flow\', \'datarole\', \'table\', \'database\', \'view\', \'workbook\'\n  - `ownerIds`: Filter by specific owner IDs (array of integers)\n  - `modifiedTime`: Filter by last modified times using ISO 8601 date-time strings. Can be either a range (with startDate/endDate) or an array of specific date-times to include\n\n- `limit` (optional): The maximum number of items to return in the search response (default: 100, max: 2000).\n\n- `orderBy` (optional): An array of `{ method, sortDirection }` objects that controls how results are sorted. If omitted, results are sorted by their "relevance score" in descending order — Tableau\'s internal ranking of how well each item matches your search terms. `sortDirection` is \'asc\' (ascending) or \'desc\' (descending) and defaults to \'asc\'. The first element is the primary sort; any additional elements are tiebreakers, applied in order. Available sorting methods:\n  - `hitsTotal`: Number of times a content item has been viewed since it was created\n  - `hitsSmallSpanTotal`: Number of times a content item was viewed in the last month\n  - `hitsMediumSpanTotal`: Number of times a content item was viewed in the last 3 months\n  - `hitsLargeSpanTotal`: Number of times a content item was viewed in the last year\n  - `downstreamWorkbookCount`: Number of workbooks in a given project. This value is only available when the content type filter includes \'database\' or \'table\'\n\n**Example Usage:**\n\n- Top 5 most-viewed workbooks (all time): `{ limit: 5, filter: { contentTypes: ["workbook"] }, orderBy: [{ method: "hitsTotal", sortDirection: "desc" }] }`\n- Free-text search for the most relevant content: `{ terms: "quarterly sales" }`\n- Find only workbooks and views matching a topic: `{ terms: "revenue", filter: { contentTypes: ["workbook", "view"] } }`\n- Surface the most-viewed datasources this month (no search terms): `{ filter: { contentTypes: ["datasource"] }, orderBy: [{ method: "hitsSmallSpanTotal", sortDirection: "desc" }] }`\n- Multi-key sort — most-viewed all-time first, using views this year as a tiebreaker: `{ orderBy: [{ method: "hitsTotal", sortDirection: "desc" }, { method: "hitsLargeSpanTotal", sortDirection: "desc" }] }`\n- Content owned by specific users, modified in a date range: `{ filter: { ownerIds: [123, 456], modifiedTime: { startDate: "2026-01-01T00:00:00Z", endDate: "2026-06-30T23:59:59Z" } } }`\n',
    inputSchema: {
      additionalProperties: false,
      properties: {
        filter: {
          anyOf: [
            {
              additionalProperties: false,
              properties: {
                contentTypes: {
                  items: {
                    enum: [
                      "lens",
                      "datasource",
                      "virtualconnection",
                      "collection",
                      "project",
                      "flow",
                      "datarole",
                      "table",
                      "database",
                      "view",
                      "workbook",
                    ],
                    type: "string",
                  },
                  minItems: 1,
                  type: "array",
                },
                modifiedTime: {
                  anyOf: [
                    {
                      items: {
                        format: "date-time",
                        type: "string",
                      },
                      minItems: 1,
                      type: "array",
                    },
                    {
                      additionalProperties: false,
                      properties: {
                        endDate: {
                          format: "date-time",
                          type: "string",
                        },
                        startDate: {
                          format: "date-time",
                          type: "string",
                        },
                      },
                      required: ["startDate"],
                      type: "object",
                    },
                    {
                      additionalProperties: false,
                      properties: {
                        endDate: {
                          format: "date-time",
                          type: "string",
                        },
                        startDate: {
                          format: "date-time",
                          type: "string",
                        },
                      },
                      required: ["endDate"],
                      type: "object",
                    },
                  ],
                },
                ownerIds: {
                  items: {
                    type: "integer",
                  },
                  minItems: 1,
                  type: "array",
                },
              },
              required: ["contentTypes"],
              type: "object",
            },
            {
              additionalProperties: false,
              properties: {
                contentTypes: {
                  items: {
                    $ref: "#/properties/filter/anyOf/0/properties/contentTypes/items",
                  },
                  minItems: 1,
                  type: "array",
                },
                modifiedTime: {
                  $ref: "#/properties/filter/anyOf/0/properties/modifiedTime",
                },
                ownerIds: {
                  items: {
                    type: "integer",
                  },
                  minItems: 1,
                  type: "array",
                },
              },
              required: ["ownerIds"],
              type: "object",
            },
            {
              additionalProperties: false,
              properties: {
                contentTypes: {
                  $ref: "#/properties/filter/anyOf/1/properties/contentTypes",
                },
                modifiedTime: {
                  $ref: "#/properties/filter/anyOf/0/properties/modifiedTime",
                },
                ownerIds: {
                  $ref: "#/properties/filter/anyOf/0/properties/ownerIds",
                },
              },
              required: ["modifiedTime"],
              type: "object",
            },
          ],
        },
        limit: {
          default: 100,
          maximum: 2000,
          minimum: 1,
          type: "integer",
        },
        orderBy: {
          items: {
            additionalProperties: false,
            properties: {
              method: {
                enum: [
                  "hitsTotal",
                  "hitsSmallSpanTotal",
                  "hitsMediumSpanTotal",
                  "hitsLargeSpanTotal",
                  "downstreamWorkbookCount",
                ],
                type: "string",
              },
              sortDirection: {
                default: "asc",
                enum: ["asc", "desc"],
                type: "string",
              },
            },
            required: ["method"],
            type: "object",
          },
          minItems: 1,
          type: "array",
        },
        terms: {
          minLength: 1,
          type: "string",
        },
      },
      type: "object",
    },
    name: "search-content",
  },
];
